// ★ COURSE IMPORT — the rules an automatic course generation obeys.
//
// ── Omar, 2026-09-18 (verbatim) ─────────────────────────────────────────────
//
//   "I want to manage the rules for automatic course generation directly from
//    the Console, so I can change how future imports work without code changes
//    or deployment. Add a Course import section, following the existing
//    Photography settings pattern for editing, saving and permissions."
//
// So this screen is deliberately the SAME SHAPE as `image-profiles.tsx`: a tab
// strip, an `Original`/`Edited` pill per setting, Save disabled until something
// actually differs, "Restore the original" offered only where there is an
// override to remove, and a last tab showing the real text sent to the model
// rather than a description of it. Omar already learned that screen; a second
// arrangement for the same act would be a second thing to learn.
//
// ── ⚠️ FOUR THINGS HERE THAT A PLAIN SETTINGS PAGE WOULD GET WRONG ──────────
//
//  1. EVERY VALUE ON SCREEN COMES FROM THE SERVER, including the factory values,
//     the choosable layouts and the prompt text. This file holds no copy of any
//     of them. The exceptions are all imported from `@dynamo/schema` and are
//     the SAME constants the gateway validates and generates with, so an input's
//     range and the server's refusal cannot disagree
//     (`feedback_one_rule_one_place`): the two bounds tables, the "no upper
//     limit" sentinel, the two kinds of screen that deliberately have no word
//     band — carried with their own reasons — and `maxConsecutiveFor`, which is
//     the one place a per-layout override and the general rule are read
//     together. Copying any of them here would create a second list that agrees
//     only while somebody remembers both.
//
//  2. A SAVE ADOPTS WHAT THE SERVER RETURNED, never what was typed. `saveRule`
//     reads `r.rules[key]` out of the response and hands that back to the
//     editor, so a value the server normalised, clamped or refused to change is
//     visible immediately instead of being papered over by the draft that
//     caused it (`feedback_a_failure_state_must_read_back_not_infer`).
//
//  3. THE "SAVED" MESSAGE OUTLIVES THE REFRESH THAT PROVES IT. Saving reloads
//     the whole response — that is how the prompt text, the pills and the
//     timestamp stay in agreement — which unmounts the editor that did the
//     saving. A message owned by that editor would therefore delete itself at
//     the moment of success, so the banner lives on the SCREEN, above the tab
//     strip (`feedback_a_result_message_must_outlive_the_refresh_that_proves_it`).
//
//  4. A STORED LAYOUT ID THE SERVER NO LONGER OFFERS IS SHOWN, NOT DROPPED.
//     Rendering only the offered choices would silently discard a stored id the
//     moment the generator retired a layout — the screen would look correct and
//     the next save would quietly delete a rule
//     (`feedback_silent_key_strip_hides_data_loss`).
//
// Omar is not a developer, so every control says what the rule DOES in plain
// words and what the original setting is, and every number says its permitted
// range on screen rather than only in a rejected save.

import { useEffect, useState } from "react";

// The only things imported from the schema are the shared constants named in
// ⚠️ 1 above, and the types. Everything else — the rules, their factory values,
// the invariants, the prompt text — arrives in the response, because the server
// is the authority on what is actually stored and what will actually be sent.
import {
  COURSE_IMPORT_LAYOUTS_WITHOUT_WORD_BAND,
  COURSE_IMPORT_NESTED_BOUNDS,
  COURSE_IMPORT_RULE_BOUNDS,
  WORD_BAND_NO_UPPER_LIMIT,
  type ComposableFieldPermissions,
  type CourseImportRuleKey,
  type CourseImportRuleSource,
  type CourseImportRules,
  type LayoutWordBand,
  type LayoutWordLimit,
  maxConsecutiveFor,
  type WordBandCandidates,
  type WordLimitDirection,
} from "@dynamo/schema";

import type {
  ApiError,
  ConsoleApi,
  CourseImportLayoutChoice,
  CourseImportRuleWrites,
  CourseImportSettings,
  ImportFinalMeasurement,
  ImportRunRow,
} from "./api.js";
import { TabPanel, Tabs } from "./tabs.js";
import { CODE_STYLE, ErrorState, InlineError, Loading, fmtDateTime, useAsync, useSubmitLatch } from "./ui.js";

/**
 * ★ SEVEN TABS, because Omar's instruction about the Photography screen was
 * "I don't want a full long page but tabs to go through each" and this screen
 * would otherwise be longer than that one was.
 *
 * The first five are the settings, grouped by the question they answer rather
 * than by the shape of the control.
 *
 * "Words the AI may write" was added 2026-09-18. It earns a tab of its own
 * rather than a row on "Layouts" because it answers a different KIND of
 * question — not how a screen is shaped, but which of its text the import is
 * allowed to author.
 *
 * ⚠️ SHORTENED 2026-09-19 on Omar's instruction, which gave the seven labels as
 * a table. Two of them — "Fixed rules" and "AI prompt" — were previously his own
 * wording carried over VERBATIM from the Photography screen, on the reasoning
 * that "the same two questions apply here and they should not have two different
 * names in one console". His new table renames them HERE ONLY, so that older
 * reasoning no longer holds and the two screens now disagree. That divergence is
 * deliberate on this side and NOT a licence to rename the Photography screen to
 * match: its labels are still his, and changing them was not asked for. It is
 * reported to him as an open choice instead
 * (`feedback_do_not_remove_a_control_the_change_made_redundant` — offer both
 * halves rather than picking one).
 */
const IMPORT_TABS = [
  { id: "structure", label: "Structure" },
  { id: "layout", label: "Layouts" },
  { id: "video", label: "Video" },
  { id: "words", label: "AI wording" },
  { id: "coverage", label: "Coverage" },
  { id: "fixed", label: "Fixed rules" },
  { id: "sent", label: "AI prompt" },
  // ★ ADDED 2026-09-21 with the findings recorder. It is the only tab that
  //   shows what imports actually DID rather than what they are told to do —
  //   which is the whole point of the test phase Omar opened: "Before any of the
  //   nine rules is enforced, I need to see how imports behave today."
  { id: "runs", label: "Import runs" },
] as const;

type ImportTabId = (typeof IMPORT_TABS)[number]["id"];

const TAB_PREFIX = "course-import";

function Note({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ margin: "0 0 10px", fontSize: 12.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
      {children}
    </p>
  );
}

// ── The editor every rule goes through ──────────────────────────────────────

interface RuleEditorProps<T> {
  /** The schema's own key. Also the hook every test uses to find this block. */
  ruleKey: CourseImportRuleKey;
  title: string;
  /** What the rule does, in the words of somebody who is not a developer. */
  description: React.ReactNode;
  /** The protected original, written out as a person reads it. */
  factoryLabel: string;
  /** What the next import would use right now. */
  value: T;
  source: CourseImportRuleSource;
  equal: (a: T, b: T) => boolean;
  /** Why this draft cannot be saved, or `null` when it can. */
  validate?: (draft: T) => string | null;
  control: (args: {
    draft: T;
    setDraft: (next: T) => void;
    busy: boolean;
  }) => React.ReactNode;
  /** `null` REMOVES the override. Resolves to what the server actually stored. */
  onSave: (next: T | null) => Promise<{ value: T; source: CourseImportRuleSource }>;
  /** Told what happened, in words the screen shows above the tab strip. */
  onSaved: (message: string) => void;
}

/**
 * ONE editor, three kinds of control.
 *
 * `ProfileLayerEditor` could not be reused — it is a long-free-text control and
 * these are numbers, switches and a multi-select — but its four properties are
 * the ones that matter and are reproduced exactly: it says where the value came
 * from, it offers Restore only when there is something to restore, Save is
 * unavailable until the value actually differs, and it reads back what the
 * server stored instead of trusting its own input.
 *
 * Generic rather than written three times, because three copies of "when is
 * Save enabled" drift and the half that drifts is the one nobody is looking at
 * (`feedback_a_test_double_that_reimplements_drifts`).
 */
function RuleEditor<T>({
  ruleKey,
  title,
  description,
  factoryLabel,
  value,
  source,
  equal,
  validate,
  control,
  onSave,
  onSaved,
}: RuleEditorProps<T>) {
  const [draft, setDraft] = useState<T>(value);
  const [current, setCurrent] = useState<{ value: T; source: CourseImportRuleSource }>({
    value,
    source,
  });
  const [err, setErr] = useState<ApiError | null>(null);
  const { run, busy } = useSubmitLatch();

  // ⚠️ Re-sync when the screen reloads the rules underneath us. Without this the
  // editor keeps rendering a stale copy of its props
  // (`feedback_a_local_mirror_of_props_goes_stale`). Keyed on the SERIALISED
  // value, not the value itself: `openingLayouts` is an array, and identity
  // would re-run this on every render that happened to rebuild it.
  const signature = JSON.stringify(value);
  useEffect(() => {
    setDraft(value);
    setCurrent({ value, source });
    setErr(null);
    // `value` is intentionally absent — `signature` is what decides whether it
    // has really changed.
  }, [signature, source]);

  const dirty = !equal(draft, current.value);
  const isFactory = current.source === "factory";
  const invalid = validate ? validate(draft) : null;

  const apply = (next: T | null) => {
    setErr(null);
    run(
      () => onSave(next),
      (stored) => {
        // ★ THE SERVER'S ANSWER, never the draft that caused it.
        setCurrent(stored);
        setDraft(stored.value);
        onSaved(
          stored.source === "factory"
            ? `${title} is back to the original setting. Future imports will use it; ` +
                `courses that already exist are unchanged.`
            : `${title} saved. Future imports will use it; courses that already exist ` +
                `are unchanged.`,
        );
      },
      (e) => setErr(e),
    );
  };

  return (
    <div data-rule={ruleKey} style={{ marginBottom: 26 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "var(--text-strong)" }}>
          {title}
        </h3>
        {/* Not decoration. "nobody has ever chosen this" and "a colleague chose
            this" look identical in a box showing a value
            (`feedback_an_empty_control_hides_the_default_it_avoids`). */}
        <span className={isFactory ? "pill pending" : "pill proposed"}>
          {isFactory ? "Original" : "Edited"}
        </span>
      </div>

      <p style={{ margin: "0 0 10px", fontSize: 12.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
        {description}
      </p>

      {control({ draft, setDraft, busy })}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginTop: 10,
        }}
      >
        <button
          type="button"
          className="btn primary"
          disabled={busy || !dirty || !!invalid}
          onClick={() => apply(draft)}
        >
          {busy ? "Saving…" : "Save"}
        </button>

        {/* Only where an override exists to remove. On the original setting this
            button would do nothing at all (`feedback_no_false_affordance_toggles`). */}
        {!isFactory && (
          <button type="button" className="btn" disabled={busy} onClick={() => apply(null)}>
            Restore the original
          </button>
        )}

        {dirty && !busy && (
          <button type="button" className="btn" onClick={() => setDraft(current.value)}>
            Discard changes
          </button>
        )}

        <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
          The original setting is <strong>{factoryLabel}</strong>.
        </span>
      </div>

      {invalid && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--error-text)" }}>
          {invalid} Save is unavailable until then — nothing has been saved.
        </p>
      )}

      {err && (
        <div style={{ marginTop: 8 }}>
          <InlineError err={err} />
        </div>
      )}
    </div>
  );
}

// ── The three kinds of control ──────────────────────────────────────────────

interface CommonRuleProps {
  source: CourseImportRuleSource;
  onSaved: (message: string) => void;
}

function NumberRule({
  ruleKey,
  title,
  description,
  bounds,
  value,
  factoryValue,
  unit,
  source,
  onSave,
  onSaved,
}: CommonRuleProps & {
  ruleKey: CourseImportRuleKey;
  title: string;
  description: React.ReactNode;
  bounds: { min: number; max: number };
  value: number;
  factoryValue: number;
  /** Singular noun, so "3 screens" reads as English and "1 screen" does too. */
  unit: string;
  onSave: (next: number | null) => Promise<{ value: number; source: CourseImportRuleSource }>;
}) {
  const say = (n: number) => `${n} ${n === 1 ? unit : `${unit}s`}`;
  return (
    <RuleEditor<number>
      ruleKey={ruleKey}
      title={title}
      description={description}
      factoryLabel={say(factoryValue)}
      value={value}
      source={source}
      equal={(a, b) => Object.is(a, b)}
      validate={(d) =>
        Number.isInteger(d) && d >= bounds.min && d <= bounds.max
          ? null
          : `Enter a whole number between ${bounds.min} and ${bounds.max}.`
      }
      control={({ draft, setDraft, busy }) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <input
            className="field focusable"
            type="number"
            inputMode="numeric"
            step={1}
            min={bounds.min}
            max={bounds.max}
            disabled={busy}
            aria-label={title}
            style={{ width: 96 }}
            // An emptied box is NaN, which the validator refuses by name rather
            // than silently becoming 0 — `0` is a legal value for two of these
            // rules and means something quite different from "blank".
            value={Number.isFinite(draft) ? String(draft) : ""}
            onChange={(e) =>
              setDraft(e.target.value === "" ? Number.NaN : Number(e.target.value))
            }
          />
          <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            Anything from {bounds.min} to {bounds.max}.
          </span>
        </div>
      )}
      onSave={onSave}
      onSaved={onSaved}
    />
  );
}

function SwitchRule({
  ruleKey,
  title,
  description,
  value,
  factoryValue,
  onText,
  offText,
  source,
  onSave,
  onSaved,
}: CommonRuleProps & {
  ruleKey: CourseImportRuleKey;
  title: string;
  description: React.ReactNode;
  value: boolean;
  factoryValue: boolean;
  /** What happens while it is on / off — shown beside the box, in plain words. */
  onText: string;
  offText: string;
  onSave: (next: boolean | null) => Promise<{ value: boolean; source: CourseImportRuleSource }>;
}) {
  return (
    <RuleEditor<boolean>
      ruleKey={ruleKey}
      title={title}
      description={description}
      factoryLabel={factoryValue ? "on" : "off"}
      value={value}
      source={source}
      equal={(a, b) => a === b}
      control={({ draft, setDraft, busy }) => (
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            fontSize: 12.5,
            lineHeight: 1.6,
            color: "var(--text)",
            cursor: "default",
            maxWidth: 680,
          }}
        >
          <input
            type="checkbox"
            className="focusable"
            // The accessible name stays the rule's own name whatever the state
            // is — a name that changes as the box is ticked is a name no test
            // and no screen reader can rely on.
            aria-label={title}
            checked={draft}
            disabled={busy}
            onChange={(e) => setDraft(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>{draft ? onText : offText}</span>
        </label>
      )}
      onSave={onSave}
      onSaved={onSaved}
    />
  );
}

/** Set equality — the stored order of a set of allowed openers means nothing. */
function sameIds(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((v, i) => v === right[i]);
}

/**
 * ⑧ Which fields the model may WRITE rather than copy, per kind of screen.
 *
 * ★ WHY IT IS A GRID AND NOT ELEVEN SWITCHES. The question a person asks here is
 * "may it write the small text under a heading?", and the answer differs by kind
 * of screen. A grid answers that in one look; eleven separate blocks would make
 * the reader hold the pattern in their head.
 *
 * ⚠️ A tick is only offered where the layout HAS that field. The gateway says
 * which — the Console keeps no list of its own — and a screen with no such field
 * shows "—", never a tick that does nothing.
 */
function ComposableRule({
  value,
  factoryValue,
  choices,
  source,
  onSave,
  onSaved,
}: CommonRuleProps & {
  value: Record<string, ComposableFieldPermissions>;
  factoryValue: Record<string, ComposableFieldPermissions>;
  choices: CourseImportLayoutChoice[];
  onSave: (
    next: Record<string, ComposableFieldPermissions> | null,
  ) => Promise<{
    value: Record<string, ComposableFieldPermissions>;
    source: CourseImportRuleSource;
  }>;
}) {
  const FIELDS = [
    { key: "title" as const, label: "Screen title" },
    { key: "subtitle" as const, label: "Text under the title" },
    { key: "labels" as const, label: "Tab / step / icon names" },
  ];
  const allows = (v: Record<string, ComposableFieldPermissions>, id: string) =>
    v[id] ?? { title: false, subtitle: false, labels: false };
  const applies = (c: CourseImportLayoutChoice, k: "title" | "subtitle" | "labels") =>
    k === "title" ? true : (c.slots ? c.slots[k] : true);

  const countOf = (v: Record<string, ComposableFieldPermissions>) =>
    choices.reduce(
      (n, c) =>
        n + FIELDS.filter((f) => applies(c, f.key) && allows(v, c.id)[f.key]).length,
      0,
    );

  return (
    <RuleEditor<Record<string, ComposableFieldPermissions>>
      ruleKey="composableFields"
      title="Words the AI may write"
      description={
        <>
          The import places the document&rsquo;s own words; it does not rewrite them. This
          says where that rule is relaxed for <strong>short labels only</strong> — a screen
          title, the line of text under it, and the names on tabs, steps and icons. Those
          have nothing to be copied from, and leaving them out is why generated screens
          used to arrive still carrying <em>Lorem ipsum</em>. Everything else &mdash; the
          body text, what is behind a tab, a video script, a question and its answers
          &mdash; always comes from the document, whatever is ticked here.
        </>
      }
      factoryLabel={`${countOf(factoryValue)} of the available fields may be written`}
      value={value}
      source={source}
      equal={(a, b) =>
        choices.every((c) =>
          FIELDS.every((f) => allows(a, c.id)[f.key] === allows(b, c.id)[f.key]),
        )
      }
      control={({ draft, setDraft, busy }) => (
        <div style={{ overflowX: "auto", maxWidth: "100%" }}>
          <table
            style={{ borderCollapse: "collapse", fontSize: 12.5, minWidth: 520 }}
          >
            <caption style={{ textAlign: "left", padding: "0 0 8px", color: "var(--text-muted)" }}>
              A dash means that kind of screen has no such field.
            </caption>
            <thead>
              <tr>
                <th scope="col" style={{ textAlign: "left", padding: "4px 16px 8px 0", fontWeight: 600 }}>
                  Kind of screen
                </th>
                {FIELDS.map((f) => (
                  <th key={f.key} scope="col" style={{ textAlign: "left", padding: "4px 16px 8px 0", fontWeight: 600 }}>
                    {f.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {choices.map((c) => (
                <tr key={c.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <th scope="row" style={{ textAlign: "left", padding: "7px 16px 7px 0", fontWeight: 400 }}>
                    {c.label}
                  </th>
                  {FIELDS.map((f) => (
                    <td key={f.key} data-label={f.label} style={{ padding: "7px 16px 7px 0" }}>
                      {applies(c, f.key) ? (
                        <input
                          type="checkbox"
                          className="focusable"
                          aria-label={`${c.label} — ${f.label}`}
                          checked={allows(draft, c.id)[f.key]}
                          disabled={busy}
                          onChange={(e) => {
                            const row = { ...allows(draft, c.id), [f.key]: e.target.checked };
                            setDraft({ ...draft, [c.id]: row });
                          }}
                        />
                      ) : (
                        <span aria-label="not available on this kind of screen">&mdash;</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      onSave={onSave}
      onSaved={onSaved}
    />
  );
}

function LayoutsRule({
  ruleKey,
  title,
  description,
  value,
  factoryValue,
  choices,
  source,
  onSave,
  onSaved,
  emptyMeans,
}: CommonRuleProps & {
  ruleKey: CourseImportRuleKey;
  /** What an EMPTY tick-list means for THIS rule — they differ, and it matters. */
  emptyMeans: string;
  title: string;
  description: React.ReactNode;
  value: string[];
  factoryValue: string[];
  choices: CourseImportLayoutChoice[];
  onSave: (next: string[] | null) => Promise<{ value: string[]; source: CourseImportRuleSource }>;
}) {
  const offered = new Set(choices.map((c) => c.id));
  // ★ A STORED ID THE SERVER NO LONGER OFFERS IS STILL SHOWN. Rendering only the
  //   offered list would drop it from the next save without anybody being told
  //   (`feedback_silent_key_strip_hides_data_loss`).
  const retired: CourseImportLayoutChoice[] = value
    .filter((id) => !offered.has(id))
    .map((id) => ({ id, label: `${id} — no longer offered` }));
  const rows = [...choices, ...retired];
  const labelOf = (id: string) => rows.find((c) => c.id === id)?.label ?? id;

  return (
    <RuleEditor<string[]>
      ruleKey={ruleKey}
      title={title}
      description={description}
      factoryLabel={
        factoryValue.length ? factoryValue.map(labelOf).join(", ") : emptyMeans
      }
      value={value}
      source={source}
      equal={sameIds}
      control={({ draft, setDraft, busy }) => (
        <>
          <div
            role="group"
            aria-label={title}
            style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px", maxWidth: 720 }}
          >
            {rows.map((c) => (
              <label
                key={c.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  fontSize: 12.5,
                  color: "var(--text)",
                  cursor: "default",
                }}
              >
                <input
                  type="checkbox"
                  className="focusable"
                  checked={draft.includes(c.id)}
                  disabled={busy}
                  onChange={(e) => {
                    const set = new Set(draft);
                    if (e.target.checked) set.add(c.id);
                    else set.delete(c.id);
                    // Held in the order the server offered them, so the sentence
                    // the model is sent reads the same way twice running.
                    setDraft(rows.filter((r) => set.has(r.id)).map((r) => r.id));
                  }}
                />
                <span>{c.label}</span>
              </label>
            ))}
          </div>
          <p
            style={{
              margin: "10px 0 0",
              fontSize: 12.5,
              lineHeight: 1.6,
              color: "var(--text-muted)",
            }}
          >
            {draft.length === 0 ? (
              <>
                <strong>Nothing is ticked, so this rule is switched off</strong> — a module may
                open with any layout the document suits.
              </>
            ) : (
              <>
                A module&rsquo;s first screen must be one of the {draft.length} ticked above.
                Untick them all to switch the rule off.
              </>
            )}
          </p>
          {retired.length > 0 && (
            <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "var(--warning-text)" }}>
              {retired.length === 1 ? "One saved layout is" : `${retired.length} saved layouts are`}{" "}
              no longer offered by the generator. They are shown so you can decide; leaving them
              ticked keeps them stored, and they have no effect.
            </p>
          )}
        </>
      )}
      onSave={onSave}
      onSaved={onSaved}
    />
  );
}

// ── The 2026-09-21 layout rules ─────────────────────────────────────────────
//
// ★ WHY THESE ARE EDITABLE TABLES OF NUMBER BOXES AND NOT TICK-LISTS. The eight
// rules below are per-layout FIGURES — a word band, a floor, a ranked order —
// and this Console is a staff tool. A plain table shows what is stored, in the
// unit it is stored in, and there is nothing to learn before reading it.
//
// ⚠️ EVERY ROW IS DRAWN FROM THE VALUE, NEVER FROM THE OFFERED LAYOUT LIST. A
// table built from `layoutChoices` would silently drop a stored row the moment
// the generator stopped offering that layout: the screen would look correct and
// the next save would delete the rule
// (`feedback_silent_key_strip_hides_data_loss`). The offered list is used only
// to LABEL a row and to say, quietly, which ids the generator does not offer —
// which is a real signal today, because `hidden_items` appears in the factory
// bands and is not in the generator's own set.
//
// ⚠️ AND NOTHING HERE IS WORDED AS A BLOCK. D5 is binding for this phase —
// *"record only. No enforcement, no user-facing output."* These rules are given
// to the AI as guidance and what an import actually did is recorded on the
// Import runs tab; no import is held back and no author is shown anything. So
// every description below says what the import is ASKED to do, never what it is
// refused.

/** A layout id as a person reads it, with the offered set as the authority. */
interface LayoutNaming {
  label: (id: string) => string;
  offered: (id: string) => boolean;
  /** Everything a person may pick: what the generator offers, plus what is stored. */
  known: (alsoStored: readonly string[]) => string[];
}

function layoutNaming(choices: CourseImportLayoutChoice[]): LayoutNaming {
  const byId = new Map(choices.map((c) => [c.id, c.label] as const));
  return {
    label: (id) => byId.get(id) ?? id,
    offered: (id) => byId.has(id),
    known: (alsoStored) => [
      ...choices.map((c) => c.id),
      ...[...new Set(alsoStored)].filter((id) => !byId.has(id)),
    ],
  };
}

/**
 * A layout's name, with a quiet marker when the generator does not offer it.
 *
 * ⚠️ Deliberately NOT the word "retired": a stored id the generator does not
 * offer is either one that has gone or one that has not arrived yet, and the
 * screen cannot tell which. Saying which way it went would be a guess.
 */
function LayoutName({ id, naming }: { id: string; naming: LayoutNaming }) {
  return (
    <>
      {naming.label(id)}
      {naming.offered(id) ? null : (
        <span style={{ color: "var(--text-faint)" }}> · not offered by the generator</span>
      )}
    </>
  );
}

const inBounds = (n: number, b: { min: number; max: number }) =>
  Number.isInteger(n) && n >= b.min && n <= b.max;

const SELECT_STYLE: React.CSSProperties = {
  fontFamily: "inherit",
  fontSize: 12.5,
  padding: "4px 6px",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
  background: "var(--surface)",
  color: "var(--text)",
};

const SMALL_BTN: React.CSSProperties = { fontSize: 11.5, padding: "2px 8px" };

const ROW_CONTROLS: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
};

const GRID_TABLE: React.CSSProperties = {
  borderCollapse: "collapse",
  fontSize: 12.5,
  minWidth: 420,
};

const GRID_CAPTION: React.CSSProperties = {
  textAlign: "left",
  padding: "0 0 8px",
  color: "var(--text-muted)",
};

const GRID_TH: React.CSSProperties = {
  textAlign: "left",
  padding: "4px 16px 8px 0",
  fontWeight: 600,
};

const GRID_ROW_TH: React.CSSProperties = {
  textAlign: "left",
  padding: "6px 16px 6px 0",
  fontWeight: 400,
};

const GRID_TD: React.CSSProperties = { padding: "6px 16px 6px 0" };

const SUBNOTE: React.CSSProperties = {
  margin: "10px 0 0",
  fontSize: 12.5,
  lineHeight: 1.6,
  color: "var(--text-muted)",
  maxWidth: 720,
};

const PLAIN_LIST: React.CSSProperties = { listStyle: "none", margin: 0, padding: 0 };

/** One number box inside a table, styled like the standalone number rules. */
function NumberCell({
  label,
  value,
  bounds,
  busy,
  onChange,
  width = 76,
}: {
  label: string;
  value: number;
  bounds: { min: number; max: number };
  busy: boolean;
  onChange: (next: number) => void;
  width?: number;
}) {
  return (
    <input
      className="field focusable"
      type="number"
      inputMode="numeric"
      step={1}
      min={bounds.min}
      max={bounds.max}
      disabled={busy}
      aria-label={label}
      style={{ width }}
      // An emptied box is NaN, refused by name — never silently 0, which is a
      // legal figure here and means something quite different from "blank".
      value={Number.isFinite(value) ? String(value) : ""}
      onChange={(e) => onChange(e.target.value === "" ? Number.NaN : Number(e.target.value))}
    />
  );
}

// ── Equality, written out per rule rather than by `JSON.stringify` ──────────
//
// ⚠️ A serialised comparison would call a map DIRTY because a key was removed
// and re-added in a different position — Save would light up on a value nobody
// changed, and "Save is unavailable until something actually differs" would
// quietly stop being true.

const sameKeySet = (a: object, b: object) => {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => k in b);
};

function sameNumberMap(a: Record<string, number>, b: Record<string, number>): boolean {
  return sameKeySet(a, b) && Object.entries(a).every(([k, v]) => Object.is(v, b[k]));
}

function sameWordBands(
  a: Record<string, LayoutWordBand>,
  b: Record<string, LayoutWordBand>,
): boolean {
  return (
    sameKeySet(a, b) &&
    Object.entries(a).every(([k, band]) => {
      const other = b[k];
      return !!other && Object.is(band.p25, other.p25) && Object.is(band.p75, other.p75);
    })
  );
}

function sameLimits(a: LayoutWordLimit[], b: LayoutWordLimit[]): boolean {
  return (
    a.length === b.length &&
    a.every((l, i) => {
      const other = b[i]!;
      return (
        l.layout === other.layout &&
        l.direction === other.direction &&
        Object.is(l.words, other.words)
      );
    })
  );
}

/** ★ ORDER IS THE MEANING here, so this comparison is order-sensitive. */
function sameBandWeights(a: WordBandCandidates[], b: WordBandCandidates[]): boolean {
  return (
    a.length === b.length &&
    a.every((row, i) => {
      const other = b[i]!;
      return (
        Object.is(row.maxWords, other.maxWords) &&
        row.layouts.length === other.layouts.length &&
        row.layouts.every((id, j) => id === other.layouts[j])
      );
    })
  );
}

function sameSequences(a: string[][], b: string[][]): boolean {
  return (
    a.length === b.length &&
    a.every((seq, i) => {
      const other = b[i] ?? [];
      return seq.length === other.length && seq.every((id, j) => id === other[j]);
    })
  );
}

/**
 * **Rule 1** — the whole-screen word band per kind of screen.
 *
 * ⚠️ D3, verbatim and binding: *"Do not convert between the two."* Every figure
 * on this block is a WORD count for a WHOLE screen. The per-field CHARACTER
 * budgets answer a different question and are deliberately not shown here, not
 * offered as a conversion, and not mentioned as a comparable number — the one
 * time this project compared a figure naming a PART with a figure naming a
 * TOTAL it inverted the conclusion
 * (`feedback_a_budget_naming_a_part_cannot_be_compared_to_a_total`).
 */
function WordBandsRule({
  value,
  factoryValue,
  choices,
  source,
  onSave,
  onSaved,
}: CommonRuleProps & {
  value: Record<string, LayoutWordBand>;
  factoryValue: Record<string, LayoutWordBand>;
  choices: CourseImportLayoutChoice[];
  onSave: (
    next: Record<string, LayoutWordBand> | null,
  ) => Promise<{ value: Record<string, LayoutWordBand>; source: CourseImportRuleSource }>;
}) {
  const naming = layoutNaming(choices);
  const bounds = COURSE_IMPORT_NESTED_BOUNDS.wordCount;
  // ★ CARRIED AS DATA, so the reason a kind of screen has no range travels with
  //   the setting instead of living in a comment somebody has to find — and so
  //   nobody "restores" a row whose absence is the point.
  const absent = Object.entries(COURSE_IMPORT_LAYOUTS_WITHOUT_WORD_BAND);

  return (
    <RuleEditor<Record<string, LayoutWordBand>>
      ruleKey="wordBands"
      title="How many words a kind of screen usually carries"
      description={
        <>
          Roughly how much text each kind of screen actually holds across the six courses that
          were measured &mdash; the middle half of them sits between the two figures. The import
          uses it to choose the kind of screen a passage suits.{" "}
          <strong>
            Both figures count words on the WHOLE screen &mdash; every word the finished screen
            shows, all its boxes added together.
          </strong>{" "}
          That answers a different question from the per-field character limit the editor applies
          to one box, and one is never turned into the other. Nothing here trims or pads the
          document: where a passage suits no range, its text is kept whole and the fact is
          recorded.
        </>
      }
      factoryLabel={`${Object.keys(factoryValue).length} kinds of screen, each with its own range`}
      value={value}
      source={source}
      equal={sameWordBands}
      validate={(draft) => {
        for (const [id, band] of Object.entries(draft)) {
          if (!inBounds(band.p25, bounds) || !inBounds(band.p75, bounds)) {
            return `${naming.label(id)}: each figure is a whole number of words from ${bounds.min} to ${bounds.max}.`;
          }
          if (band.p25 > band.p75) {
            return `${naming.label(id)}: the first figure cannot be above the second.`;
          }
        }
        return null;
      }}
      control={({ draft, setDraft, busy }) => (
        <div style={{ overflowX: "auto", maxWidth: "100%" }}>
          <table style={GRID_TABLE}>
            <caption style={GRID_CAPTION}>
              Both columns are counts of words on a whole screen.
            </caption>
            <thead>
              <tr>
                <th scope="col" style={GRID_TH}>
                  Kind of screen
                </th>
                <th scope="col" style={GRID_TH}>
                  Typical words &mdash; from
                </th>
                <th scope="col" style={GRID_TH}>
                  Typical words &mdash; to
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(draft).map(([id, band]) => (
                <tr key={id} style={{ borderTop: "1px solid var(--border)" }}>
                  <th scope="row" style={GRID_ROW_TH}>
                    <LayoutName id={id} naming={naming} />
                  </th>
                  <td style={GRID_TD} data-label="Typical words — from">
                    <NumberCell
                      label={`${naming.label(id)} — typical words, from`}
                      value={band.p25}
                      bounds={bounds}
                      busy={busy}
                      onChange={(n) => setDraft({ ...draft, [id]: { ...band, p25: n } })}
                    />
                  </td>
                  <td style={GRID_TD} data-label="Typical words — to">
                    <NumberCell
                      label={`${naming.label(id)} — typical words, to`}
                      value={band.p75}
                      bounds={bounds}
                      busy={busy}
                      onChange={(n) => setDraft({ ...draft, [id]: { ...band, p75: n } })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {absent.length > 0 && (
            <>
              <p style={SUBNOTE}>
                <strong>
                  {absent.length} kinds of screen deliberately have no range of their own
                </strong>
                , and the reason is kept with the setting rather than in somebody&rsquo;s head:
              </p>
              <ul style={{ ...SUBNOTE, paddingLeft: 18, margin: "6px 0 0" }}>
                {absent.map(([id, why]) => (
                  <li key={id} style={{ marginBottom: 4 }}>
                    <strong>{naming.label(id)}</strong> &mdash; {why}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      onSave={onSave}
      onSaved={onSaved}
    />
  );
}

/**
 * **Rule 1's three clean figures** — each with the direction it bounds.
 *
 * ⚠️ THE DIRECTION IS SHOWN AS A SENTENCE, not left to be inferred from the
 * number. "34 words on a full-screen video" and "63 words behind a tab strip"
 * are the same shape of figure pointing opposite ways, and a reader who has to
 * work out which will eventually work it out wrong — silently, and while
 * editing.
 */
function HardLimitsRule({
  value,
  factoryValue,
  choices,
  source,
  onSave,
  onSaved,
}: CommonRuleProps & {
  value: LayoutWordLimit[];
  factoryValue: LayoutWordLimit[];
  choices: CourseImportLayoutChoice[];
  onSave: (
    next: LayoutWordLimit[] | null,
  ) => Promise<{ value: LayoutWordLimit[]; source: CourseImportRuleSource }>;
}) {
  const naming = layoutNaming(choices);
  const bounds = COURSE_IMPORT_NESTED_BOUNDS.wordCount;
  const say = (l: LayoutWordLimit) =>
    `${naming.label(l.layout)} — never ${l.direction === "never_above" ? "above" : "below"} ${
      Number.isFinite(l.words) ? l.words : "?"
    } words`;

  return (
    <RuleEditor<LayoutWordLimit[]>
      ruleKey="wordBandHardLimits"
      title="Word figures the measured courses never crossed"
      description={
        <>
          Figures that held on every screen of the six courses that were measured, each with the
          direction it bounds. The import takes them into account when it chooses a kind of
          screen.{" "}
          <strong>
            They describe those six courses. They are not limits the Player enforces &mdash;
            nothing breaks at 35 words on a full-screen video, it has simply never been tried.
          </strong>{" "}
          Treating a measured figure as a rule is a choice, and this is where the choice is made.
        </>
      }
      factoryLabel={factoryValue.length ? factoryValue.map(say).join(" · ") : "nothing listed"}
      value={value}
      source={source}
      equal={sameLimits}
      validate={(draft) => {
        for (const l of draft) {
          if (!inBounds(l.words, bounds)) {
            return `${naming.label(l.layout)}: a figure is a whole number of words from ${bounds.min} to ${bounds.max}.`;
          }
        }
        return null;
      }}
      control={({ draft, setDraft, busy }) => {
        const known = naming.known(draft.map((l) => l.layout));
        const set = (i: number, next: LayoutWordLimit) =>
          setDraft(draft.map((l, j) => (j === i ? next : l)));
        return (
          <>
            <ul style={PLAIN_LIST}>
              {draft.map((l, i) => (
                <li key={i} data-limit={l.layout} style={{ marginBottom: 12 }}>
                  {/* ★ THE RULE AS A SENTENCE, read back from the draft, so what
                      is about to be saved is legible without reading three
                      controls left to right. */}
                  <div style={{ fontSize: 12.5, marginBottom: 4 }}>
                    <strong data-testid={`limit-${l.layout}`}>{say(l)}</strong>
                    {naming.offered(l.layout) ? null : (
                      <span style={{ color: "var(--text-faint)" }}>
                        {" "}
                        · not offered by the generator
                      </span>
                    )}
                  </div>
                  <div style={ROW_CONTROLS}>
                    <select
                      className="focusable"
                      style={SELECT_STYLE}
                      aria-label={`Figure ${i + 1} — kind of screen`}
                      disabled={busy}
                      value={l.layout}
                      onChange={(e) => set(i, { ...l, layout: e.target.value })}
                    >
                      {known.map((id) => (
                        <option key={id} value={id}>
                          {naming.label(id)}
                        </option>
                      ))}
                    </select>
                    <select
                      className="focusable"
                      style={SELECT_STYLE}
                      aria-label={`Figure ${i + 1} — which way it bounds`}
                      disabled={busy}
                      value={l.direction}
                      onChange={(e) =>
                        set(i, { ...l, direction: e.target.value as WordLimitDirection })
                      }
                    >
                      <option value="never_above">never above</option>
                      <option value="never_below">never below</option>
                    </select>
                    <NumberCell
                      label={`Figure ${i + 1} — words`}
                      value={l.words}
                      bounds={bounds}
                      busy={busy}
                      onChange={(n) => set(i, { ...l, words: n })}
                    />
                    <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>words</span>
                    <button
                      type="button"
                      className="btn"
                      style={SMALL_BTN}
                      disabled={busy}
                      aria-label={`Remove figure ${i + 1}`}
                      onClick={() => setDraft(draft.filter((_, j) => j !== i))}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="btn"
              style={SMALL_BTN}
              disabled={busy || known.length === 0}
              onClick={() =>
                setDraft([...draft, { layout: known[0]!, direction: "never_above", words: 1 }])
              }
            >
              Add a figure
            </button>
            {draft.length === 0 && (
              <p style={SUBNOTE}>
                <strong>Nothing is listed, so this rule is switched off</strong> &mdash; no word
                figure of this kind is taken into account.
              </p>
            )}
          </>
        );
      }}
      onSave={onSave}
      onSaved={onSaved}
    />
  );
}

/**
 * **Rule 2** — the ranked candidates per band.
 *
 * ★ THE ORDER IS THE RULE. The first name in a band is the one that wins; the
 * next is reached for only when the one before it cannot be used. So the
 * position is numbered on screen and moved with two buttons, rather than being
 * implied by where a name happens to sit in a list.
 *
 * ⚠️ ONLY THE TOP OF EACH BAND IS STORED and the band below it starts one word
 * higher. Two stored edges could disagree about where they meet, and a gap
 * between them is a word count that matches no band at all — which reads
 * downstream as "no candidate" rather than as a broken table.
 */
function BandWeightsRule({
  value,
  factoryValue,
  choices,
  source,
  onSave,
  onSaved,
}: CommonRuleProps & {
  value: WordBandCandidates[];
  factoryValue: WordBandCandidates[];
  choices: CourseImportLayoutChoice[];
  onSave: (
    next: WordBandCandidates[] | null,
  ) => Promise<{ value: WordBandCandidates[]; source: CourseImportRuleSource }>;
}) {
  const naming = layoutNaming(choices);
  const bounds = { min: 1, max: COURSE_IMPORT_NESTED_BOUNDS.wordCount.max };

  return (
    <RuleEditor<WordBandCandidates[]>
      ruleKey="bandWeights"
      title="Which kind of screen wins, by length"
      description={
        <>
          When more than one kind of screen would suit a passage, this decides the order they are
          tried in. <strong>The first name in a band is the first choice</strong>; the next is
          reached for only when the one before it cannot be used &mdash; for instance because it
          would repeat the screen before it. The bands are read in order and each one starts
          where the one above it ends, so only the top of each band is stored. This never reshapes
          a passage to land it in a different band.
        </>
      }
      factoryLabel={`${factoryValue.length} bands, each with its own order of preference`}
      value={value}
      source={source}
      equal={sameBandWeights}
      validate={(draft) => {
        for (let i = 0; i < draft.length; i += 1) {
          const row = draft[i]!;
          if (row.layouts.length === 0) {
            return `Band ${i + 1} has no kind of screen in it — a band with nothing in it can never be chosen from.`;
          }
          if (row.maxWords !== WORD_BAND_NO_UPPER_LIMIT && !inBounds(row.maxWords, bounds)) {
            return `Band ${i + 1}: the top of a band is a whole number of words from ${bounds.min} to ${bounds.max}.`;
          }
          // The same ascending rule the gateway refuses a save for. It is
          // checked here so Save is not offered for a body the server will
          // reject — the decision still lives in the schema.
          if (i > 0 && row.maxWords <= draft[i - 1]!.maxWords) {
            return `Band ${i + 1} must end above the band before it — each band starts one word after the one above.`;
          }
        }
        return null;
      }}
      control={({ draft, setDraft, busy }) => {
        const known = naming.known(draft.flatMap((b) => b.layouts));
        const setBand = (i: number, next: WordBandCandidates) =>
          setDraft(draft.map((b, j) => (j === i ? next : b)));
        const reorder = (i: number, from: number, to: number) => {
          const row = draft[i]!;
          const layouts = [...row.layouts];
          const [moved] = layouts.splice(from, 1);
          layouts.splice(to, 0, moved!);
          setBand(i, { ...row, layouts });
        };
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {draft.map((band, i) => {
              const remaining = known.filter((id) => !band.layouts.includes(id));
              return (
                <div
                  key={i}
                  data-band={i}
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-sm)",
                    padding: "10px 12px",
                    maxWidth: 720,
                  }}
                >
                  <div style={ROW_CONTROLS}>
                    <strong style={{ fontSize: 12.5 }}>Band {i + 1}</strong>
                    {band.maxWords === WORD_BAND_NO_UPPER_LIMIT ? (
                      <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                        everything longer &mdash; no upper limit
                      </span>
                    ) : (
                      <>
                        <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>up to</span>
                        <NumberCell
                          label={`Band ${i + 1} — up to how many words`}
                          value={band.maxWords}
                          bounds={bounds}
                          busy={busy}
                          onChange={(n) => setBand(i, { ...band, maxWords: n })}
                        />
                        <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>words</span>
                      </>
                    )}
                  </div>
                  <ol style={{ ...PLAIN_LIST, marginTop: 8 }}>
                    {band.layouts.map((id, j) => (
                      <li key={id} style={{ ...ROW_CONTROLS, marginBottom: 4 }}>
                        {/* ★ THE POSITION, WRITTEN OUT. A list marker is styling;
                            the number is the meaning. */}
                        <span style={{ fontSize: 12.5, width: 22, color: "var(--text-muted)" }}>
                          {j + 1}.
                        </span>
                        <span style={{ fontSize: 12.5, minWidth: 190 }}>
                          <LayoutName id={id} naming={naming} />
                        </span>
                        <button
                          type="button"
                          className="btn"
                          style={SMALL_BTN}
                          disabled={busy || j === 0}
                          aria-label={`Move ${naming.label(id)} up in band ${i + 1}`}
                          onClick={() => reorder(i, j, j - 1)}
                        >
                          Move up
                        </button>
                        <button
                          type="button"
                          className="btn"
                          style={SMALL_BTN}
                          disabled={busy || j === band.layouts.length - 1}
                          aria-label={`Move ${naming.label(id)} down in band ${i + 1}`}
                          onClick={() => reorder(i, j, j + 1)}
                        >
                          Move down
                        </button>
                        <button
                          type="button"
                          className="btn"
                          style={SMALL_BTN}
                          disabled={busy}
                          aria-label={`Remove ${naming.label(id)} from band ${i + 1}`}
                          onClick={() =>
                            setBand(i, {
                              ...band,
                              layouts: band.layouts.filter((other) => other !== id),
                            })
                          }
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ol>
                  {remaining.length > 0 && (
                    <select
                      className="focusable"
                      style={{ ...SELECT_STYLE, marginTop: 6 }}
                      aria-label={`Add a kind of screen to band ${i + 1}`}
                      disabled={busy}
                      value=""
                      onChange={(e) => {
                        if (!e.target.value) return;
                        setBand(i, { ...band, layouts: [...band.layouts, e.target.value] });
                      }}
                    >
                      <option value="">Add a kind of screen…</option>
                      {remaining.map((id) => (
                        <option key={id} value={id}>
                          {naming.label(id)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              );
            })}
          </div>
        );
      }}
      onSave={onSave}
      onSaved={onSaved}
    />
  );
}

/**
 * **Rule 3** — the approved three-screen module openings.
 *
 * a) b) c) are the labels the AI is given for the same shapes, so the letters on
 * screen and the letters in the instruction are the same letters.
 */
const OPENING_LETTERS = "abcdefgh".split("");

function OpeningSequencesRule({
  value,
  factoryValue,
  choices,
  source,
  onSave,
  onSaved,
}: CommonRuleProps & {
  value: string[][];
  factoryValue: string[][];
  choices: CourseImportLayoutChoice[];
  onSave: (
    next: string[][] | null,
  ) => Promise<{ value: string[][]; source: CourseImportRuleSource }>;
}) {
  const naming = layoutNaming(choices);
  const letter = (i: number) => OPENING_LETTERS[i] ?? String(i + 1);

  return (
    <RuleEditor<string[][]>
      ruleKey="openingSequences"
      title="Approved ways to open a module"
      description={
        <>
          The three-screen shapes a module may open with. The import is asked to choose by what
          the second screen&rsquo;s content actually is &mdash; something a person would say out
          loud, parallel material, or an explanation that moves in steps. This is about the first
          three screens together; the rule above is about the first screen alone, and a module can
          satisfy one without satisfying the other. Where the document cannot fill a shape, the
          module is built from what the document supports and the shortfall is recorded &mdash;
          no screen is ever invented to complete one.
        </>
      }
      factoryLabel={
        factoryValue.length
          ? `${factoryValue.length} approved openings`
          : "nothing listed, so the rule is off"
      }
      value={value}
      source={source}
      equal={sameSequences}
      validate={(draft) => {
        for (const [i, seq] of draft.entries()) {
          if (seq.length === 0) {
            return `Opening ${letter(i)} has no screens in it.`;
          }
        }
        return null;
      }}
      control={({ draft, setDraft, busy }) => {
        const known = naming.known(draft.flat());
        return (
          <>
            <ul style={PLAIN_LIST}>
              {draft.map((seq, i) => (
                <li key={i} style={{ ...ROW_CONTROLS, marginBottom: 8 }} data-opening={i}>
                  <strong style={{ fontSize: 12.5, width: 22 }}>{letter(i)})</strong>
                  {seq.map((id, j) => (
                    <span key={j} style={ROW_CONTROLS}>
                      {j > 0 && (
                        <span aria-hidden="true" style={{ color: "var(--text-faint)" }}>
                          &rarr;
                        </span>
                      )}
                      <select
                        className="focusable"
                        style={SELECT_STYLE}
                        aria-label={`Opening ${letter(i)} — screen ${j + 1}`}
                        disabled={busy}
                        value={id}
                        onChange={(e) =>
                          setDraft(
                            draft.map((other, oi) =>
                              oi === i
                                ? other.map((step, si) => (si === j ? e.target.value : step))
                                : other,
                            ),
                          )
                        }
                      >
                        {known.map((choice) => (
                          <option key={choice} value={choice}>
                            {naming.label(choice)}
                          </option>
                        ))}
                      </select>
                    </span>
                  ))}
                  <button
                    type="button"
                    className="btn"
                    style={SMALL_BTN}
                    disabled={busy}
                    aria-label={`Remove opening ${letter(i)}`}
                    onClick={() => setDraft(draft.filter((_, oi) => oi !== i))}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="btn"
              style={SMALL_BTN}
              disabled={busy || known.length === 0}
              onClick={() => setDraft([...draft, [known[0]!, known[0]!, known[0]!]])}
            >
              Add an opening
            </button>
            {draft.length === 0 && (
              <p style={SUBNOTE}>
                <strong>Nothing is listed, so this rule is switched off</strong> &mdash; a module
                may open with any shape the document suits.
              </p>
            )}
          </>
        );
      }}
      onSave={onSave}
      onSaved={onSaved}
    />
  );
}

/**
 * A figure kept PER KIND OF SCREEN, over a rule that already has an answer for
 * every other kind.
 *
 * ★ WHY THE FALLBACK IS A COLUMN AND NOT A FOOTNOTE. Two controls that look
 * independent and are not is a false affordance: a person who sets the general
 * rule to 2 and cannot see which kinds of screen ignore it has been told half
 * the rule. So every known kind of screen has a row, and a row with no figure
 * of its own SAYS what applies to it instead of showing a blank
 * (`feedback_absence_and_emptiness_read_the_same`).
 */
function LayoutNumberMapRule({
  ruleKey,
  title,
  description,
  bounds,
  ownFigureLabel,
  effective,
  defaultFigure,
  factoryLabel,
  value,
  factoryValue,
  choices,
  source,
  onSave,
  onSaved,
}: CommonRuleProps & {
  ruleKey: CourseImportRuleKey;
  title: string;
  description: React.ReactNode;
  bounds: { min: number; max: number };
  /** The column heading for the number, in the unit it is counted in. */
  ownFigureLabel: string;
  /** What actually applies to this kind of screen, named or not. */
  effective: (id: string, draft: Record<string, number>) => string;
  /** What a newly ticked row starts at when the factory has no figure for it. */
  defaultFigure: number;
  factoryLabel: string;
  value: Record<string, number>;
  factoryValue: Record<string, number>;
  choices: CourseImportLayoutChoice[];
  onSave: (
    next: Record<string, number> | null,
  ) => Promise<{ value: Record<string, number>; source: CourseImportRuleSource }>;
}) {
  const naming = layoutNaming(choices);
  // ⚠️ From the stored and factory values as well as the offered list, and
  // computed OUTSIDE the control: unticking a row must not make the row vanish.
  const rows = naming.known([...Object.keys(value), ...Object.keys(factoryValue)]);

  return (
    <RuleEditor<Record<string, number>>
      ruleKey={ruleKey}
      title={title}
      description={description}
      factoryLabel={factoryLabel}
      value={value}
      source={source}
      equal={sameNumberMap}
      validate={(draft) => {
        for (const [id, n] of Object.entries(draft)) {
          if (!inBounds(n, bounds)) {
            return `${naming.label(id)}: enter a whole number between ${bounds.min} and ${bounds.max}.`;
          }
        }
        return null;
      }}
      control={({ draft, setDraft, busy }) => {
        const all = [...new Set([...rows, ...Object.keys(draft)])];
        return (
          <div style={{ overflowX: "auto", maxWidth: "100%" }}>
            <table style={GRID_TABLE}>
              <caption style={GRID_CAPTION}>
                A kind of screen with no figure of its own is not left undecided &mdash; the last
                column says what applies to it.
              </caption>
              <thead>
                <tr>
                  <th scope="col" style={GRID_TH}>
                    Kind of screen
                  </th>
                  <th scope="col" style={GRID_TH}>
                    Its own figure
                  </th>
                  <th scope="col" style={GRID_TH}>
                    {ownFigureLabel}
                  </th>
                  <th scope="col" style={GRID_TH}>
                    What applies
                  </th>
                </tr>
              </thead>
              <tbody>
                {all.map((id) => {
                  const own = Object.prototype.hasOwnProperty.call(draft, id);
                  return (
                    <tr key={id} style={{ borderTop: "1px solid var(--border)" }}>
                      <th scope="row" style={GRID_ROW_TH}>
                        <LayoutName id={id} naming={naming} />
                      </th>
                      <td style={GRID_TD} data-label="Its own figure">
                        <input
                          type="checkbox"
                          className="focusable"
                          aria-label={`${naming.label(id)} — give it a figure of its own`}
                          checked={own}
                          disabled={busy}
                          onChange={(e) => {
                            const next = { ...draft };
                            if (e.target.checked) next[id] = factoryValue[id] ?? defaultFigure;
                            else delete next[id];
                            setDraft(next);
                          }}
                        />
                      </td>
                      <td style={GRID_TD} data-label={ownFigureLabel}>
                        {own ? (
                          <NumberCell
                            label={`${naming.label(id)} — ${ownFigureLabel}`}
                            value={draft[id]!}
                            bounds={bounds}
                            busy={busy}
                            onChange={(n) => setDraft({ ...draft, [id]: n })}
                          />
                        ) : (
                          <span aria-label="no figure of its own">&mdash;</span>
                        )}
                      </td>
                      <td style={GRID_TD} data-label="What applies" data-effective={id}>
                        {effective(id, draft)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }}
      onSave={onSave}
      onSaved={onSaved}
    />
  );
}

/**
 * **D6** — the answer counts a question the AI writes may carry.
 *
 * ⚠️ D6, verbatim: *"The 2/3 alternation is not a constraint — do not encode it.
 * Instrument it."* So this control offers the permitted COUNTS and nothing
 * else. There is deliberately nowhere here to say how often each should be
 * used: that is recorded from real imports and settled on the data.
 */
function AnswerCountsRule({
  value,
  factoryValue,
  source,
  onSave,
  onSaved,
}: CommonRuleProps & {
  value: number[];
  factoryValue: number[];
  onSave: (next: number[] | null) => Promise<{ value: number[]; source: CourseImportRuleSource }>;
}) {
  const bounds = COURSE_IMPORT_NESTED_BOUNDS.answerCount;
  const offered: number[] = [];
  for (let n = bounds.min; n <= bounds.max; n += 1) offered.push(n);
  const say = (counts: readonly number[]) =>
    counts.length ? `${[...counts].sort((a, b) => a - b).join(" or ")} answers` : "nothing ticked";

  return (
    <RuleEditor<number[]>
      ruleKey="sequenceQuestionAnswerCounts"
      title="How many answers a question the AI writes may have"
      description={
        <>
          When the import writes a question itself on a step-by-step screen, this is how many
          answers it may offer. Exactly one of them is right, and the feedback says why.{" "}
          <strong>
            The pattern is not enforced: nothing makes the import alternate between two answers
            and three.
          </strong>{" "}
          Which count gets used, and how often, is recorded from real imports and looked at
          afterwards. And this applies only to a question the import writes: a question the
          document supplies keeps its own answers, however many it has.
        </>
      }
      factoryLabel={say(factoryValue)}
      value={value}
      source={source}
      equal={(a, b) =>
        a.length === b.length &&
        [...a].sort((x, y) => x - y).every((n, i) => n === [...b].sort((x, y) => x - y)[i])
      }
      validate={(draft) =>
        draft.length === 0
          ? "Tick at least one count — a question the import writes has to offer some answers."
          : null
      }
      control={({ draft, setDraft, busy }) => (
        <div role="group" aria-label="Permitted answer counts" style={ROW_CONTROLS}>
          {offered.map((n) => (
            <label
              key={n}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                fontSize: 12.5,
                color: "var(--text)",
                cursor: "default",
              }}
            >
              <input
                type="checkbox"
                className="focusable"
                aria-label={`${n} answers`}
                checked={draft.includes(n)}
                disabled={busy}
                onChange={(e) => {
                  const set = new Set(draft);
                  if (e.target.checked) set.add(n);
                  else set.delete(n);
                  // Ascending, so the sentence the AI is given reads the same
                  // way whichever order the boxes were ticked in.
                  setDraft([...set].sort((a, b) => a - b));
                }}
              />
              <span>{n} answers</span>
            </label>
          ))}
        </div>
      )}
      onSave={onSave}
      onSaved={onSaved}
    />
  );
}


/** One "what can never be changed" entry, straight from the response. */

/**
 * ── THE IMPORT RUNS TAB ─────────────────────────────────────────────────────
 *
 * ★ WHY IT EXISTS, in Omar's words (2026-09-21): *"Every field recorded must be
 * reachable through a reader, not only by direct SQL against the database. This
 * project already carries a field recorded for months that no report exposes —
 * durationMs in ai_usage.detail. A field nobody can read is not recorded.
 * Deliver at least a per-run view or export I can open."*
 *
 * So this panel is the deliverable, not a nicety. It lists runs, opens one, and
 * hands over a CSV of EVERY recorded column.
 *
 * ⚠️ NOTHING HERE IS SHOWN TO AN AUTHOR. Omar's D5 puts end-user notification
 * explicitly out of scope for this phase — *"I will decide it when this feature
 * is implemented."* This is the staff view, in the Console, and the authoring
 * app renders none of it.
 */
function ImportRunsPanel({ api }: { api: ConsoleApi }) {
  const [openRun, setOpenRun] = useState<string | null>(null);
  const runs = useAsync(() => api.courseImportRuns(25), []);

  if (runs.state.t === "loading") return <Loading label="Reading the recorded runs" />;
  if (runs.state.t === "err") return <ErrorState err={runs.state.err} onRetry={runs.retry} />;
  const list = runs.state.data.runs;

  return (
    <>
      <Note>
        What imports have actually done. These are observations, not failures: nothing here
        blocked an import, nothing was held back, and no author was shown anything. The
        point of this phase is the data.
      </Note>
      <Note>
        {/* ★ THE SENTENCE THAT EXPLAINS THE WHOLE COLUMN. Omar&rsquo;s import
            recorded 41 screens and the course he opened held 70; both numbers
            were real and nothing compared them. */}
        <strong>Two numbers, not one.</strong> <em>Planned</em> is what the AI proposed before
        a single screen existed. <em>Saved</em> is what the author actually opens, counted
        after every recovery and repair. The difference between them is the interesting part
        — and where it says <em>not measured</em>, nobody counted the saved course, which is
        not the same as counting zero.
      </Note>
      {list.length === 0 ? (
        <Note>
          {/* ⚠️ "No runs yet" and "the recorder is broken" are different facts and
              this screen must not render them the same way. */}
          No import has been recorded in this environment yet. A run appears here the moment
          one finishes — if you have run an import since this was switched on and nothing is
          listed, that is worth reporting rather than waiting.
        </Note>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--text-faint)" }}>
              <th style={TH}>When</th>
              <th style={TH}>Course</th>
              <th style={TH}>Where</th>
              {/* ★ ONE COLUMN CARRYING BOTH NUMBERS. It is a single column on
                  purpose: two adjacent columns can be read one at a time, and
                  reading one at a time is how 41 and 70 coexisted for a day. */}
              <th style={TH}>Screens — planned → saved</th>
              <th style={TH}>Document kept</th>
              <th style={TH}>Findings</th>
              <th style={TH}>Rules</th>
              <th style={TH} />
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <RunRow
                key={r.runId}
                run={r}
                api={api}
                open={openRun === r.runId}
                onToggle={() => setOpenRun(openRun === r.runId ? null : r.runId)}
              />
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

const TH: React.CSSProperties = {
  padding: "6px 8px", borderBottom: "1px solid var(--border)", fontWeight: 500,
};
const TD: React.CSSProperties = { padding: "6px 8px", verticalAlign: "top" };

/**
 * ── ONE RUN'S ROW ───────────────────────────────────────────────────────────
 *
 * ★ TWO MEASUREMENTS, SIDE BY SIDE, OR NEITHER.
 *
 * This row used to print `summary.screens` on its own. On 2026-09-20 Omar's
 * first real import recorded **41 screens** and the course he opened held
 * **70**. Both numbers were real, neither was a lie, and nothing in the product
 * compared them — so the reassuring one was the one on the screen
 * (`feedback_a_coverage_number_can_measure_the_plan_not_the_result`).
 *
 * So the cell now shows the plan, the saved course, and the GAP between them,
 * and the gap is the thing drawn in bold: it is the finding.
 */
function RunRow({
  run, api, open, onToggle,
}: { run: ImportRunRow; api: ConsoleApi; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr style={{ borderBottom: "1px solid var(--border)" }}>
        <td style={TD} data-label="When">{fmtDateTime(run.startedAt)}</td>
        <td style={{ ...TD, fontFamily: "var(--mono, monospace)", fontSize: 11 }} data-label="Course">
          {run.courseId.slice(0, 8)}
        </td>
        <td style={TD} data-label="Where">{run.environment}</td>
        <td style={TD} data-label="Screens">
          <ScreensCell run={run} />
        </td>
        <td style={TD} data-label="Document kept">
          <RetentionCell final={run.final ?? null} />
        </td>
        {/* ★ THE DENOMINATOR TRAVELS WITH THE COUNT. "14 findings" over 5 screens
            and over 500 are different statements — and since 0027 the count is
            also split by WHICH course it describes, because two observations
            about the plan while the saved course was never examined is a very
            different answer from one of each. */}
        <td style={TD} data-label="Findings">
          <strong>{run.findings}</strong>
          <div style={{ color: "var(--text-faint)", fontSize: 11 }} data-testid="findings-by-phase">
            {run.planFindings ?? 0} on the plan · {run.finalFindings ?? 0} on the saved course
          </div>
          {run.summary?.perClassification ? (
            <div style={{ color: "var(--text-faint)", fontSize: 11 }}>
              {run.summary.perClassification["ai_avoidable"] ?? 0} AI-avoidable ·{" "}
              {run.summary.perClassification["document_constrained"] ?? 0} document ·{" "}
              {run.summary.perClassification["undetermined"] ?? 0} undetermined
            </div>
          ) : null}
        </td>
        {/* The rules fingerprint. Two runs are comparable only if it matches —
            which is what makes "same configuration" something you can see. The
            snapshot BEHIND the fingerprint is opened in the detail below. */}
        <td style={{ ...TD, fontFamily: "var(--mono, monospace)", fontSize: 11 }} data-label="Rules">
          {run.rulesHash}
          <div style={{ color: "var(--text-faint)" }}>{run.rulesSource}</div>
        </td>
        <td style={TD} data-label="">
          <button type="button" onClick={onToggle} style={LINK_BTN}>
            {open ? "Hide" : "Open"}
          </button>
        </td>
      </tr>
      {open ? (
        <tr>
          <td colSpan={8} style={{ ...TD, background: "var(--surface-2, transparent)" }} data-label="Findings">
            <RunDetail api={api} run={run} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * `41 planned → 70 saved (+29)`.
 *
 * ⚠️ WHEN NOBODY MEASURED THE SAVED COURSE THIS SAYS SO IN THOSE WORDS.
 * It never prints 0 and never prints a dash: a dash reads as zero at a glance,
 * and zero is a measurement that found nothing rather than a measurement that
 * never happened (`feedback_absence_and_emptiness_read_the_same`).
 *
 * The planned figure falls back to `summary.screens` for a run recorded before
 * migration 0027 promoted it out of the blob — the number was always there, and
 * refusing to show it would invent a second kind of absence.
 */
function ScreensCell({ run }: { run: ImportRunRow }) {
  const planned = run.plan?.screens ?? run.summary?.screens ?? null;
  const saved = run.final?.savedScreens ?? null;
  const gap =
    planned === null || saved === null ? null : (run.screensAddedAfterPlan ?? saved - planned);
  return (
    <>
      <div>
        <span data-testid="planned-screens">
          {planned === null ? "not recorded" : `${planned} planned`}
        </span>
        <span style={{ color: "var(--text-faint)" }}> → </span>
        <span
          data-testid="saved-screens"
          data-measured={run.final ? "yes" : "no"}
          style={run.final ? undefined : { color: "var(--text-faint)", fontStyle: "italic" }}
        >
          {/* ★ THE EXACT WORDS. Not "—", not "0". */}
          {saved === null ? "not measured" : `${saved} saved`}
        </span>
        {gap === null ? null : (
          <>
            {" "}
            {/* ★ THE GAP IS THE FINDING, so it is the thing that stands out. */}
            <strong
              data-testid="screens-gap"
              style={{ color: gap === 0 ? "var(--text-faint)" : "var(--warning-text)" }}
            >
              {gap === 0 ? "(no change)" : `(${gap > 0 ? "+" : "−"}${Math.abs(gap)})`}
            </strong>
          </>
        )}
      </div>
      <div style={{ color: "var(--text-faint)", fontSize: 11 }}>
        {run.plan?.modules ?? run.summary?.modules ?? "not recorded"} planned modules
        {run.final?.savedModules === null || run.final?.savedModules === undefined
          ? ""
          : ` · ${run.final.savedModules} saved`}
      </div>
    </>
  );
}

/** A fraction as a percentage to one decimal place. Null stays null. */
const pct = (n: number | null): string | null =>
  n === null || Number.isNaN(n) ? null : `${(n * 100).toFixed(1)}%`;

/**
 * ★ HOW MUCH OF THE DOCUMENT IS ACTUALLY IN THE COURSE — WITH ITS CONTROL.
 *
 * The control is the same course measured against unrelated text, which must
 * score near zero. It is the whole reason the first number can be trusted: a
 * matcher that had stopped discriminating would report a perfect result and
 * nothing on this screen would disagree
 * (`feedback_a_probe_without_a_control_cannot_fail_honestly`).
 *
 * ⚠️ SO THE CONTROL IS STRUCTURALLY INSEPARABLE HERE. There is no branch that
 * renders the percentage alone: when the control was not recorded, the slot
 * says so in words, because a bare percentage presented as trustworthy is the
 * defect, not the missing number.
 */
function RetentionCell({ final }: { final: ImportFinalMeasurement | null }) {
  if (!final) {
    return (
      <span
        data-testid="retention"
        data-retention="not-measured"
        style={{ color: "var(--text-faint)", fontStyle: "italic" }}
      >
        not measured
      </span>
    );
  }
  // ★ "IT DID NOT RUN" IS ITS OWN OUTCOME, listed before any number, and it
  //   carries the reason rather than a score of zero.
  if (!final.retentionRan || final.retentionFraction === null) {
    return (
      <span data-testid="retention" data-retention="did-not-run" style={{ color: "var(--text-faint)" }}>
        did not run — {final.retentionReason || "no reason was recorded"}
      </span>
    );
  }
  const control = pct(final.retentionControl);
  return (
    <span data-testid="retention" data-retention="measured">
      <strong>{pct(final.retentionFraction)}</strong>{" "}
      <span style={{ color: "var(--text-faint)", fontSize: 11 }}>
        (control {control ?? "not recorded — this figure cannot stand on its own"})
      </span>
      {final.retentionTarget === null ? null : (
        <div style={{ color: "var(--text-faint)", fontSize: 11 }}>target {pct(final.retentionTarget)}</div>
      )}
      {final.blocksTotal === null ? null : (
        <div style={{ color: "var(--text-faint)", fontSize: 11 }}>
          {final.blocksAbsent ?? 0} of {final.blocksTotal} passages on no screen
        </div>
      )}
    </span>
  );
}

const LINK_BTN: React.CSSProperties = {
  background: "none", border: "none", padding: 0, cursor: "pointer",
  color: "var(--accent)", font: "inherit", textDecoration: "underline",
};

/** One opened run: where it came from, what it produced, what it kept. */
function RunDetail({ api, run }: { api: ConsoleApi; run: ImportRunRow }) {
  return (
    <>
      <RunProvenance run={run} />
      <RunFinalMeasurement api={api} runId={run.runId} />
      <RunEvidence api={api} runId={run.runId} />
      <RunRulesSnapshot run={run} />
      <RunFindings api={api} runId={run.runId} />
    </>
  );
}

const SECTION_H: React.CSSProperties = {
  margin: "14px 0 6px", fontSize: 12, fontWeight: 600, color: "var(--text-strong)",
  textTransform: "uppercase", letterSpacing: 0.4,
};

const FAINT: React.CSSProperties = { color: "var(--text-faint)", fontSize: 12 };

/**
 * ── WHAT WENT IN ────────────────────────────────────────────────────────────
 *
 * Omar's evidence items ① and ②: which file, which version of the instructions,
 * and whether anything was left out on the way in. A course that is missing a
 * third of its document because the extraction stopped at 90,000 characters
 * looks identical, on every other screen, to one the planner simply ignored.
 */
function RunProvenance({ run }: { run: ImportRunRow }) {
  const src = run.source ?? null;
  const sha = src?.sha256 ?? null;
  return (
    <>
      <h4 style={SECTION_H}>The source</h4>
      <div style={{ fontSize: 12.5, lineHeight: 1.7 }}>
        <div>
          <strong data-testid="source-name">{src?.name || "not recorded"}</strong>{" "}
          {/* The first 12 characters are enough to tell two uploads apart and
              short enough to sit on one line. The whole digest is in the CSV. */}
          <span style={{ ...FAINT, fontFamily: "var(--mono, monospace)" }} data-testid="source-sha">
            {sha ? sha.slice(0, 12) : "no fingerprint recorded"}
          </span>
        </div>
        <div style={FAINT}>
          {src?.blocks === null || src?.blocks === undefined ? "block count not recorded" : `${src.blocks} blocks`}
          {src?.chars === null || src?.chars === undefined ? "" : ` · ${src.chars.toLocaleString()} characters`}
          {src?.tocSkipped ? ` · ${src.tocSkipped} skipped as contents/index` : ""}
          {src?.truncated ? (
            <strong style={{ color: "var(--warning-text)" }}>
              {" "}· the extraction was TRUNCATED, so the figures above describe what was read,
              not what the file holds
            </strong>
          ) : null}
        </div>
        <div style={FAINT}>
          Instructions version:{" "}
          <span style={{ fontFamily: "var(--mono, monospace)" }} data-testid="prompt-version">
            {run.promptVersion || "not recorded"}
          </span>
          {run.plannerInput?.chars === null || run.plannerInput?.chars === undefined
            ? ""
            : ` · the planner was sent ${run.plannerInput.chars.toLocaleString()} characters`}
          {run.plannerInput?.truncated ? (
            <strong style={{ color: "var(--warning-text)" }}>
              {" "}· and that input was CUT to fit, so the planner never saw the whole document
            </strong>
          ) : null}
        </div>
      </div>
    </>
  );
}

/**
 * ── WHAT THE AUTHOR ACTUALLY GOT ────────────────────────────────────────────
 *
 * The same measurement the row summarises, in full, from its own reader — which
 * is also the only place the "nobody measured this" sentence is spelled out at
 * length rather than in two words.
 */
function RunFinalMeasurement({ api, runId }: { api: ConsoleApi; runId: string }) {
  const got = useAsync(() => api.courseImportFinal(runId), [runId]);
  if (got.state.t === "loading") return <Loading label="Reading the saved course's measurement" />;
  if (got.state.t === "err") return <ErrorState err={got.state.err} onRetry={got.retry} />;
  const d = got.state.data;
  return (
    <>
      <h4 style={SECTION_H}>The saved course</h4>
      {!d.measured || !d.final ? (
        // ⚠️ A SENTENCE, NOT A SILENCE AND NOT A ZERO.
        <p style={{ ...FAINT, margin: 0, lineHeight: 1.7 }} data-testid="final-not-measured">
          {d.notMeasured ||
            "This run has no final measurement. It is not a measurement of zero — nothing was counted."}
        </p>
      ) : (
        <div style={{ fontSize: 12.5, lineHeight: 1.7 }} data-testid="final-measured">
          <div>
            <strong>{d.final.savedScreens}</strong> screens ·{" "}
            <strong>{d.final.savedModules}</strong> modules ·{" "}
            <strong>{d.final.savedChapters}</strong> module groups, counted by walking the saved
            draft on {fmtDateTime(d.final.recordedAt)}.
          </div>
          <div style={FAINT}>
            {d.final.recoveredPlacements ?? 0} passages were put back by the recovery pass ·{" "}
            {d.final.unresolvedPlacements ?? 0} could not be placed
            {d.final.blocksExcluded === null || d.final.blocksExcluded === undefined
              ? ""
              : ` · ${d.final.blocksExcluded} were excluded before planning began`}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * ── WHAT THIS RUN KEPT ──────────────────────────────────────────────────────
 *
 * ★ Omar, 2026-09-21, verbatim: *"Provide the extraction, exact document input
 * to the planner, original AI architecture response before software changes,
 * and the adjusted architecture for the same identified run. If a historical
 * raw response was not retained, say so and capture it in the next verification
 * run. Do not present regenerated output as historical evidence."* And: *"The
 * findings CSV is not a substitute for an architecture export."*
 *
 * ⚠️ EVERY KIND GETS A ROW, INCLUDING THE ONES THAT DO NOT EXIST. A list that
 * silently omits what was never captured reads as a complete list of four
 * things that happened to be two.
 */
const ARTIFACT_LABEL: Record<string, string> = {
  extraction: "The document, as the importer read it",
  planner_input: "Exactly what the planner was sent",
  model_response_raw: "The AI's original architecture, before any software adjustment",
  outline_shaped: "The architecture after every software adjustment",
};

/**
 * Why an absent artefact is absent — in plain words, per kind.
 *
 * ★ The `model_response_raw` sentence is the load-bearing one and is Omar's own
 * requirement turned into a statement a person reads on the screen. It says
 * what was lost AND why re-running would not recover it, because the tempting
 * repair — generate one now and show it — would be presenting a different
 * answer as this run's
 * (`feedback_proving_the_request_is_not_proving_the_result`).
 */
const ARTIFACT_ABSENT: Record<string, string> = {
  extraction:
    "not retained — this run predates artefact capture. Re-reading the file today " +
    "would show how the importer reads it NOW, which is not what this run was given.",
  planner_input:
    "not retained — this run predates artefact capture. The message that would be " +
    "built today is assembled from the rules as they stand now, not as they stood then.",
  model_response_raw:
    "not retained — this run predates artefact capture, and a regenerated response " +
    "would be a different response.",
  outline_shaped:
    "not retained — this run predates artefact capture. The saved course has since " +
    "been through recovery, repair and any edit the author made, so it is not this.",
};

const ARTIFACT_ABSENT_FALLBACK =
  "not retained — this run predates artefact capture, and anything produced now " +
  "would describe a different run.";

/** Bytes in the units a person reads, with the exact figure kept in the title. */
function humanBytes(n: number): string {
  if (n < 1024) return `${n} bytes`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function RunEvidence({ api, runId }: { api: ConsoleApi; runId: string }) {
  const got = useAsync(() => api.courseImportArtifacts(runId), [runId]);
  const [err, setErr] = useState<string | null>(null);

  if (got.state.t === "loading") return <Loading label="Reading what this run kept" />;
  if (got.state.t === "err") return <ErrorState err={got.state.err} onRetry={got.retry} />;
  const data = got.state.data;
  // The closed set comes from the SERVER, so this screen keeps no second copy
  // of "which four" (`feedback_one_rule_one_place`).
  const kinds = data.kinds?.length ? data.kinds : Object.keys(ARTIFACT_LABEL);
  const missing = data.missing ?? kinds.filter((k) => !data.artifacts.some((a) => a.kind === k));

  /**
   * ⚠️ FETCHED THEN SAVED AS A BLOB, not a plain link — the endpoint needs a
   * bearer token and a bare `<a href>` would answer 401, which reads as a
   * broken export rather than a missing header.
   */
  const download = async (kind: string, mediaType: string) => {
    setErr(null);
    try {
      const text = await api.courseImportArtifact(runId, kind);
      const url = URL.createObjectURL(new Blob([text], { type: `${mediaType};charset=utf-8` }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `import-${kind}-${runId}.${mediaType.includes("json") ? "json" : "txt"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "The artefact could not be downloaded.");
    }
  };

  return (
    <>
      <h4 style={SECTION_H}>Evidence</h4>
      <p style={{ ...FAINT, margin: "0 0 8px", lineHeight: 1.7 }} data-testid="evidence-count">
        {missing.length === 0
          ? `All ${kinds.length} artefacts were kept for this run.`
          : `${data.artifacts.length} of ${kinds.length} artefacts were kept — ` +
            `${missing.length} ${missing.length === 1 ? "was" : "were"} never captured.`}
      </p>
      {err ? (
        <div className="pill error" style={{ marginBottom: 8 }}>
          The artefact could not be downloaded: {err}
        </div>
      ) : null}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
        <tbody>
          {kinds.map((kind) => {
            const a = data.artifacts.find((x) => x.kind === kind);
            return (
              <tr key={kind} style={{ borderBottom: "1px solid var(--border)" }} data-artifact={kind}>
                <td style={{ ...TD, width: "40%" }} data-label="Artefact">
                  <div>{ARTIFACT_LABEL[kind] ?? kind}</div>
                  <div style={{ ...FAINT, fontFamily: "var(--mono, monospace)", fontSize: 11 }}>
                    {kind}
                  </div>
                </td>
                {a ? (
                  <>
                    <td style={TD} data-label="Size">
                      <span title={`${a.bytes} bytes`}>{humanBytes(a.bytes)}</span>
                      {/* ⚠️ `bytes` IS THE WHOLE ARTEFACT and the stored copy
                          holds less of it. Saying so beside the size is the
                          difference between a fragment and a fragment that
                          admits it (`feedback_absence_and_emptiness_read_the_same`). */}
                      {a.truncated ? (
                        <strong
                          data-testid={`truncated-${kind}`}
                          style={{ color: "var(--warning-text)" }}
                        >
                          {" "}— TRUNCATED: the stored copy is shorter than the
                          {" "}{humanBytes(a.bytes)} it describes
                        </strong>
                      ) : null}
                      <div style={{ ...FAINT, fontFamily: "var(--mono, monospace)", fontSize: 11 }}>
                        {a.sha256 ? a.sha256.slice(0, 12) : ""}
                      </div>
                    </td>
                    <td style={{ ...TD, textAlign: "right" }} data-label="">
                      <button
                        type="button"
                        style={LINK_BTN}
                        onClick={() => void download(a.kind, a.mediaType)}
                      >
                        Download
                      </button>
                    </td>
                  </>
                ) : (
                  // ★ A REASON, NOT A BLANK ROW AND NOT AN OMITTED ONE.
                  <td style={{ ...TD, ...FAINT, lineHeight: 1.7 }} colSpan={2} data-label="Kept?" data-testid={`absent-${kind}`}>
                    {ARTIFACT_ABSENT[kind] ?? ARTIFACT_ABSENT_FALLBACK}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

/**
 * ── THE RULES THIS RUN WAS JUDGED BY ────────────────────────────────────────
 *
 * ★ Recorded on every run since migration 0026 and returned by NOTHING until
 * 0027. The screen showed an eight-character fingerprint, and when two runs
 * disagreed there was no way to see HOW. Collapsed by default because it is
 * long and is consulted rather than read.
 */
function RunRulesSnapshot({ run }: { run: ImportRunRow }) {
  const rules = run.rules ?? null;
  return (
    <>
      <h4 style={SECTION_H}>The rules in force</h4>
      {rules === null ? (
        <p style={{ ...FAINT, margin: 0 }} data-testid="rules-absent">
          No snapshot was stored with this run — only its fingerprint,{" "}
          <span style={{ fontFamily: "var(--mono, monospace)" }}>{run.rulesHash}</span>. The
          rules cannot be reconstructed from it.
        </p>
      ) : (
        <details data-testid="rules-snapshot">
          <summary style={{ cursor: "pointer", fontSize: 12.5 }}>
            Show the {run.rulesSource} rules this run was judged by (fingerprint{" "}
            <span style={{ fontFamily: "var(--mono, monospace)" }}>{run.rulesHash}</span>)
          </summary>
          <pre style={{ ...CODE_STYLE, marginTop: 8, maxHeight: 320, overflow: "auto" }}>
            {JSON.stringify(rules, null, 2)}
          </pre>
        </details>
      )}
    </>
  );
}

/**
 * ── THE FINDINGS, LABELLED BY WHICH COURSE THEY DESCRIBE ────────────────────
 *
 * ⚠️ IT DEFAULTS TO BOTH, ON PURPOSE. Defaulting to either phase would hide the
 * gap between them, and the gap is the point: a run can carry six observations
 * about a plan and none at all about the course the author opened, which is
 * exactly the state that stayed invisible until 2026-09-21
 * (`feedback_a_coverage_number_can_measure_the_plan_not_the_result`).
 */
const PHASE_LABEL: Record<string, string> = {
  plan: "Plan",
  final: "Saved course",
};

type PhaseFilter = "all" | "plan" | "final";

function RunFindings({ api, runId }: { api: ConsoleApi; runId: string }) {
  const got = useAsync(() => api.courseImportFindings(runId), [runId]);
  const [csvErr, setCsvErr] = useState<string | null>(null);
  const [phase, setPhase] = useState<PhaseFilter>("all");

  if (got.state.t === "loading") return <Loading label="Reading this run’s findings" />;
  if (got.state.t === "err") return <ErrorState err={got.state.err} onRetry={got.retry} />;
  const all = got.state.data.findings;
  const planCount = all.filter((f) => f.phase !== "final").length;
  const finalCount = all.filter((f) => f.phase === "final").length;
  const rows =
    phase === "all"
      ? all
      : phase === "final"
        ? all.filter((f) => f.phase === "final")
        : all.filter((f) => f.phase !== "final");

  /**
   * ⚠️ FETCHED THEN SAVED AS A BLOB, not a plain link. The endpoint needs a
   * bearer token; an `<a href>` to it answers 401, which would look like a
   * broken export rather than a missing header.
   */
  const download = async () => {
    setCsvErr(null);
    try {
      const csv = await api.courseImportFindingsCsv(runId);
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `import-findings-${runId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setCsvErr(e instanceof Error ? e.message : "The export could not be produced.");
    }
  };

  const FILTERS: { id: PhaseFilter; label: string }[] = [
    { id: "all", label: `Both (${all.length})` },
    { id: "plan", label: `Plan (${planCount})` },
    { id: "final", label: `Saved course (${finalCount})` },
  ];

  return (
    <>
      <h4 style={SECTION_H}>Findings</h4>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
        <div role="group" aria-label="Which course the findings describe" style={{ display: "flex", gap: 6 }}>
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              aria-pressed={phase === f.id}
              onClick={() => setPhase(f.id)}
              style={{
                font: "inherit", fontSize: 12, padding: "2px 8px", cursor: "pointer",
                borderRadius: 4, border: "1px solid var(--border)",
                background: phase === f.id ? "var(--accent)" : "transparent",
                color: phase === f.id ? "var(--text-on-accent)" : "inherit",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => void download()} style={LINK_BTN}>
          Download every column as CSV
        </button>
        <span style={{ color: "var(--text-faint)", fontSize: 12 }}>
          {rows.length} {rows.length === 1 ? "finding" : "findings"} shown
        </span>
      </div>
      {/* ⚠️ Plain text, not <InlineError>: that component takes an ApiError and
          this failure is a blob/DOM one. Dressing it up as an API error would
          send the reader looking at the gateway. */}
      {csvErr ? (
        <div className="pill error" style={{ marginBottom: 8 }}>
          The export could not be produced: {csvErr}
        </div>
      ) : null}
      {finalCount === 0 ? (
        // ⚠️ "nothing was wrong with the saved course" and "nobody looked at the
        //    saved course" are different facts, and an empty filter renders them
        //    identically (`feedback_absence_and_emptiness_read_the_same`).
        <p style={{ ...FAINT, margin: "0 0 8px" }} data-testid="no-final-findings">
          Nothing was recorded about the saved course for this run. That is not the same as
          the saved course being clean — for a run before 2026-09-21 nobody examined it.
        </p>
      ) : null}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ textAlign: "left", color: "var(--text-faint)" }}>
            <th style={TH}>Describes</th>
            <th style={TH}>Where</th>
            <th style={TH}>Screen</th>
            <th style={TH}>What was observed</th>
            <th style={TH}>Made by</th>
            <th style={TH}>Size</th>
            <th style={TH}>Class</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f, i) => (
            <tr key={i} style={{ borderBottom: "1px solid var(--border)" }} data-phase={f.phase}>
              {/* ★ EVERY ROW SAYS WHICH COURSE IT IS ABOUT, whether or not the
                  filter is on — a list that only distinguishes them when asked
                  is a list that reads as one thing by default. */}
              <td style={TD} data-label="Describes">
                <span className="pill">{PHASE_LABEL[f.phase] ?? f.phase}</span>
              </td>
              <td style={TD} data-label="Where">
                {f.moduleIndex < 0 ? (
                  <em style={{ color: "var(--text-faint)" }}>whole course</em>
                ) : (
                  <>
                    {f.moduleTitle || `module ${f.moduleIndex + 1}`}
                    {/* ⚠️ `null` is "not about a screen" and prints as nothing —
                        never as 0, which would name the first screen. */}
                    {f.screenOrdinal === null ? null : ` · screen ${f.screenOrdinal + 1}`}
                  </>
                )}
              </td>
              <td style={TD} data-label="Screen">
                {f.screenTitle || <span style={{ color: "var(--text-faint)" }}>—</span>}
                <div style={{ color: "var(--text-faint)" }}>{f.screenType}</div>
              </td>
              <td style={TD} data-label="What was observed">
                <div style={{ fontFamily: "var(--mono, monospace)", fontSize: 11 }}>{f.ruleId}</div>
                <div>{f.produced}</div>
                <div style={{ color: "var(--text-faint)" }}>expected: {f.expected}</div>
              </td>
              <td style={TD} data-label="Made by">
                {f.originStage}
                {f.retypedByStage ? (
                  <div style={{ color: "var(--text-faint)" }}>then {f.retypedByStage}</div>
                ) : null}
              </td>
              {/* ★ WORDS AND CHARACTERS SIDE BY SIDE, neither derived from the
                  other, and the measured band shown as a band rather than a
                  limit (Omar's D3: "do not convert between the two"). */}
              <td style={TD} data-label="Size">
                {f.blockWords === null ? "—" : `${f.blockWords} words`}
                <div style={{ color: "var(--text-faint)" }}>
                  {f.blockChars === null ? "" : `${f.blockChars} chars`}
                  {f.layoutCapacityChars === null ? "" : ` of ${f.layoutCapacityChars}`}
                </div>
                {f.bandWordsP25 === null ? null : (
                  <div style={{ color: "var(--text-faint)" }}>
                    typical {f.bandWordsP25}–{f.bandWordsP75} words
                  </div>
                )}
              </td>
              <td style={TD} data-label="Class" title={f.classificationReason}>
                {f.classification.replace(/_/g, " ")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function Invariant({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "var(--text-strong)" }}>
          {title}
        </h3>
        <span className="pill">Fixed</span>
      </div>
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
        {body}
      </p>
    </div>
  );
}

// ── The screen ──────────────────────────────────────────────────────────────

export function CourseImportScreen({ api }: { api: ConsoleApi }) {
  const { state, retry } = useAsync<CourseImportSettings>(() => api.courseImportRules(), []);
  const [tab, setTab] = useState<ImportTabId>("structure");
  const [saveNote, setSaveNote] = useState<string | null>(null);

  /**
   * ★ ONE key at a time, and the ANSWER comes back out of the response.
   *
   * Sending a single key is what makes "restore this one rule" expressible at
   * all: everything absent from the body is left exactly as it was, so a save
   * here can never overwrite a change a colleague made to a different rule in
   * the meantime.
   */
  const saveRule =
    <K extends CourseImportRuleKey>(key: K) =>
    async (next: CourseImportRules[K] | null) => {
      const r = await api.saveCourseImportRules({ [key]: next } as CourseImportRuleWrites);
      return { value: r.rules[key], source: r.sources[key] };
    };

  const announce = (message: string) => {
    setSaveNote(message);
    // Re-read everything. The prompt text on the last tab, the pills, and the
    // "last changed" line are all derived from what is stored, and a screen
    // where one of them has moved and the others have not is worse than one
    // that reloads.
    retry();
  };

  const lastChanged = (data: CourseImportSettings) => (
    <Note>
      Last changed: {fmtDateTime(data.updatedAt)}
      {data.updatedBySub ? " · by a member of Dynamo staff" : ""}.
    </Note>
  );

  const body = (data: CourseImportSettings) => {
    const common = (key: CourseImportRuleKey) => ({
      source: data.sources[key],
      onSaved: announce,
    });

    switch (tab) {
      case "structure":
        return (
          <>
            <Note>
              How a generated course is divided up. These shape the <strong>next</strong> import;
              nothing here changes a course that already exists.
            </Note>
            <NumberRule
              {...common("minScreensPerModule")}
              ruleKey="minScreensPerModule"
              title="Minimum screens in a module"
              description="How small a module is allowed to be. The import chooses where one module ends and the next begins so that none is smaller than this — it does not add screens to reach the number."
              bounds={COURSE_IMPORT_RULE_BOUNDS.minScreensPerModule}
              value={data.rules.minScreensPerModule}
              factoryValue={data.factory.minScreensPerModule}
              unit="screen"
              onSave={saveRule("minScreensPerModule")}
            />
            <LayoutsRule
              {...common("openingLayouts")}
              title="How a module may open"
              description="The kinds of screen a module's first screen is allowed to be. Tick the ones that may open a module."
              ruleKey="openingLayouts"
              emptyMeans="nothing ticked, so the rule is off"
              value={data.rules.openingLayouts}
              factoryValue={data.factory.openingLayouts}
              choices={data.layoutChoices}
              onSave={saveRule("openingLayouts")}
            />
            {/* ★ DIRECTLY BELOW THE RULE IT SITS BESIDE. One is about the first
                screen, the other about the first three together; a module can
                satisfy either without the other, and reading them apart is how
                two rules about one place start being taken for one rule. */}
            <OpeningSequencesRule
              {...common("openingSequences")}
              value={data.rules.openingSequences}
              factoryValue={data.factory.openingSequences}
              choices={data.layoutChoices}
              onSave={saveRule("openingSequences")}
            />
            <SwitchRule
              {...common("varyLayoutsAcrossModules")}
              ruleKey="varyLayoutsAcrossModules"
              title="Vary the pattern between modules"
              description="The rule above stops the same kind of screen appearing twice in a row, and a course can still obey it by repeating one three-screen pattern in every module — which is what happens without this. With it on, the import is asked to reach for the wider set of screen kinds where the content supports it, and to make one module read differently from the next. It never splits an idea up or invents content to do it."
              value={data.rules.varyLayoutsAcrossModules}
              factoryValue={data.factory.varyLayoutsAcrossModules}
              onText="On — modules are asked to differ from one another."
              offText="Off — only the no-two-in-a-row rule applies."
              onSave={saveRule("varyLayoutsAcrossModules")}
            />
            <LayoutsRule
              {...common("allowedLayouts")}
              ruleKey="allowedLayouts"
              emptyMeans="nothing ticked, which means every kind of screen is allowed"
              title="Which kinds of screen the import may use"
              description="The catalogue the import chooses from. Tick nothing to allow all of them — that is the original setting. Tick some to narrow it, for instance while a particular kind of screen is being reworked."
              value={data.rules.allowedLayouts}
              factoryValue={data.factory.allowedLayouts}
              choices={data.layoutChoices}
              onSave={saveRule("allowedLayouts")}
            />
            <NumberRule
              {...common("maxConsecutiveSameLayout")}
              ruleKey="maxConsecutiveSameLayout"
              title="The same kind of screen in a row"
              description="How many screens of the same kind may sit next to each other, counting across the join between two modules — a learner walking the course does not notice where one module ended. One means never two the same in a row."
              bounds={COURSE_IMPORT_RULE_BOUNDS.maxConsecutiveSameLayout}
              value={data.rules.maxConsecutiveSameLayout}
              factoryValue={data.factory.maxConsecutiveSameLayout}
              unit="screen"
              onSave={saveRule("maxConsecutiveSameLayout")}
            />
            {/* ★ IMMEDIATELY UNDER THE RULE IT OVERRIDES, and its last column is
                computed by `maxConsecutiveFor` — the same function the import
                uses — so the screen cannot disagree with the generator about
                what a kind of screen nobody named actually gets. */}
            <LayoutNumberMapRule
              {...common("maxConsecutiveByLayout")}
              ruleKey="maxConsecutiveByLayout"
              title="Kinds of screen allowed to repeat more often"
              description={
                <>
                  An exception to <strong>&ldquo;The same kind of screen in a row&rdquo;</strong>{" "}
                  above, for the kinds of screen that read perfectly well two or three in a row.
                  A kind of screen with no figure of its own follows that general rule &mdash;
                  these two settings are one rule read together, never two independent ones.
                </>
              }
              bounds={COURSE_IMPORT_NESTED_BOUNDS.layoutRepeat}
              ownFigureLabel="Most in a row"
              defaultFigure={data.rules.maxConsecutiveSameLayout}
              effective={(id, draft) => {
                const n = maxConsecutiveFor(
                  { ...data.rules, maxConsecutiveByLayout: draft },
                  id,
                );
                const run = `${n} in a row`;
                return Object.prototype.hasOwnProperty.call(draft, id)
                  ? run
                  : `${run} — follows the general rule`;
              }}
              factoryLabel={
                Object.keys(data.factory.maxConsecutiveByLayout).length
                  ? Object.entries(data.factory.maxConsecutiveByLayout)
                      .map(
                        ([id, n]) =>
                          `${data.layoutChoices.find((c) => c.id === id)?.label ?? id} up to ${n} in a row`,
                      )
                      .join(" · ")
                  : "no exceptions"
              }
              value={data.rules.maxConsecutiveByLayout}
              factoryValue={data.factory.maxConsecutiveByLayout}
              choices={data.layoutChoices}
              onSave={saveRule("maxConsecutiveByLayout")}
            />
            {lastChanged(data)}
          </>
        );

      case "words":
        return (
          <>
            <Note>
              What the import may <strong>write</strong>, as opposed to copy out of the
              document. This shapes the <strong>next</strong> import; nothing here changes a
              course that already exists.
            </Note>
            <ComposableRule
              {...common("composableFields")}
              value={data.rules.composableFields}
              factoryValue={data.factory.composableFields}
              choices={data.layoutChoices}
              onSave={saveRule("composableFields")}
            />
            <SwitchRule
              {...common("composeSequenceQuestions")}
              ruleKey="composeSequenceQuestions"
              title="A closing question on a step-by-step screen"
              description="The one question the import ever writes for itself. When a step-by-step screen's steps add up to something worth checking, the screen can end with a question about them and a step that explains the answer — two extra steps, written from the meaning of the steps before them. Everything else in a course is the document's own words. This never affects the document's OWN questions: where the source carries a quiz it is still used word for word, on a quiz screen, whichever way this is set. And a question is never written at the cost of the document: if the steps already fill the screen, the text stays and the question is dropped."
              value={data.rules.composeSequenceQuestions}
              factoryValue={data.factory.composeSequenceQuestions}
              onText="On — a step-by-step screen may end with a question the import writes, and the step that explains it."
              offText="Off — no question is ever written. Step-by-step screens carry only the document's own steps."
              onSave={saveRule("composeSequenceQuestions")}
            />
            <AnswerCountsRule
              {...common("sequenceQuestionAnswerCounts")}
              value={data.rules.sequenceQuestionAnswerCounts}
              factoryValue={data.factory.sequenceQuestionAnswerCounts}
              onSave={saveRule("sequenceQuestionAnswerCounts")}
            />
            {lastChanged(data)}
          </>
        );

      case "layout":
        return (
          <>
            <Note>
              Rules about how an individual screen is put together.
            </Note>
            <SwitchRule
              {...common("sourceQuestionsUseQuizLayout")}
              ruleKey="sourceQuestionsUseQuizLayout"
              title="The document's questions become one quiz screen"
              description="When the document already contains questions — a quiz, a knowledge check — all of them are collected onto ONE quiz screen, in the document's order, each in its own question slot with its options, its correct answers and its feedback exactly as the document gives them. Questions are never spread one per screen and never placed in any other kind of screen — a question's options are not tabs or steps — even where the rules on the Structure tab would otherwise reshape that part of the course. Where the document marks no correct answer, the question is kept and flagged for the author to mark; nothing is invented."
              value={data.rules.sourceQuestionsUseQuizLayout}
              factoryValue={data.factory.sourceQuestionsUseQuizLayout}
              onText="On — the document's questions are collected into one quiz screen, kept intact."
              offText="Off — the structure rules may place question content in other kinds of screen."
              onSave={saveRule("sourceQuestionsUseQuizLayout")}
            />
            <SwitchRule
              {...common("alternateImagePlacement")}
              ruleKey="alternateImagePlacement"
              title="Alternating image placement"
              description="Where the picture sits on a text-and-image screen. With this on, successive screens of that kind in one module put the picture on the left, then the right, then the left again, so a module does not read as one long column."
              value={data.rules.alternateImagePlacement}
              factoryValue={data.factory.alternateImagePlacement}
              onText="On — pictures alternate left and right down a module."
              offText="Off — every picture sits on the same side."
              onSave={saveRule("alternateImagePlacement")}
            />
            <NumberRule
              {...common("twoColumnsMinColumnChars")}
              ruleKey="twoColumnsMinColumnChars"
              title="Minimum text in one column"
              description="A two-column screen is only a comparison if both columns say something. This is the least amount of the document's own words each column must carry. The import uses it twice: before writing, to decide whether a passage genuinely divides into two parts — which it may do inside a single paragraph, so one paragraph is not on its own a reason to rule the layout out — and after writing, to check that the two columns really are two. A passage that will not divide takes a different kind of screen instead of leaving a column blank. Zero switches the rule off."
              bounds={COURSE_IMPORT_RULE_BOUNDS.twoColumnsMinColumnChars}
              value={data.rules.twoColumnsMinColumnChars}
              factoryValue={data.factory.twoColumnsMinColumnChars}
              unit="character"
              onSave={saveRule("twoColumnsMinColumnChars")}
            />
            <NumberRule
              {...common("tabTitleMaxChars")}
              ruleKey="tabTitleMaxChars"
              title="Longest tab title"
              description="A tab button is only so wide, and a title past this length spills out of it. This is the most characters a tab name may have on a tabbed screen — spaces counted, accents counted once. The import asks for a shorter name up front, and where one still comes back too long it drops whole words from the end, never part of a word and never an ellipsis. It will not shorten a title if doing so would lose a word that appears nowhere else on that tab, leave less than half of it, or drop a 'not' and reverse what it says: those are left long and listed instead. The heading's full wording always stays in the tab's own text, so nothing is lost from the course. Zero switches the rule off."
              bounds={COURSE_IMPORT_RULE_BOUNDS.tabTitleMaxChars}
              value={data.rules.tabTitleMaxChars}
              factoryValue={data.factory.tabTitleMaxChars}
              unit="character"
              onSave={saveRule("tabTitleMaxChars")}
            />
            <SwitchRule
              {...common("balanceLayoutsWithinModule")}
              ruleKey="balanceLayoutsWithinModule"
              title="Spread layouts evenly within a module"
              description="After a module's first three screens, each new screen prefers the kind of screen that module has used least so far, among the kinds that can carry that passage without trimming or padding it. Where two are equally unused, the ranked order for that length decides. The two video kinds count as one."
              value={data.rules.balanceLayoutsWithinModule}
              factoryValue={data.factory.balanceLayoutsWithinModule}
              onText="On — a module varies the kinds of screen it uses."
              offText="Off — the ranked order alone decides."
              onSave={saveRule("balanceLayoutsWithinModule")}
            />
            <SwitchRule
              {...common("applyBandOrderToModelChoices")}
              ruleKey="applyBandOrderToModelChoices"
              title="Re-choose screens the AI picked outside their word band"
              description="The ranked order for a passage's length normally only reaches screens the import creates or changes for another reason. With this on it also revisits the AI's own choices — but only where the settings do not rank that kind of screen for that length at all, never merely because it was not the first choice. Off by default: the AI has read the passage and may have judged the layout deliberately, and this overrides that judgement."
              value={data.rules.applyBandOrderToModelChoices}
              factoryValue={data.factory.applyBandOrderToModelChoices}
              onText="On — the AI's out-of-band choices are re-chosen."
              offText="Off — the AI's own choices stand."
              onSave={saveRule("applyBandOrderToModelChoices")}
            />
            <NumberRule
              {...common("verticalTabsMinBodyChars")}
              ruleKey="verticalTabsMinBodyChars"
              title="Minimum text behind a vertical tab"
              description="A tab strip is only worth clicking if there is something behind each tab. This is the least amount of the document's own words a tab must carry — counted as real text, with formatting ignored. Where the source cannot fill them, the screen becomes a different kind rather than being padded out. Zero switches the rule off."
              bounds={COURSE_IMPORT_RULE_BOUNDS.verticalTabsMinBodyChars}
              value={data.rules.verticalTabsMinBodyChars}
              factoryValue={data.factory.verticalTabsMinBodyChars}
              unit="character"
              onSave={saveRule("verticalTabsMinBodyChars")}
            />
            {/* ★ THE D5 SENTENCE, once, above the five settings it governs.
                "record only. No enforcement, no user-facing output." Saying it
                per control would be five copies of one rule; saying it nowhere
                would let a table of figures read as a gate
                (`feedback_one_rule_one_place`). */}
            <Note>
              <strong>How long a screen&rsquo;s text usually is, and which kind of screen suits
              which length.</strong>{" "}
              These came from measuring six real courses. They are guidance the import is given,
              and what each import actually did with them is recorded on the{" "}
              <strong>Import runs</strong> tab. Nothing here holds an import back, hides anything
              from an author, or changes a course that already exists.
            </Note>
            <WordBandsRule
              {...common("wordBands")}
              value={data.rules.wordBands}
              factoryValue={data.factory.wordBands}
              choices={data.layoutChoices}
              onSave={saveRule("wordBands")}
            />
            <HardLimitsRule
              {...common("wordBandHardLimits")}
              value={data.rules.wordBandHardLimits}
              factoryValue={data.factory.wordBandHardLimits}
              choices={data.layoutChoices}
              onSave={saveRule("wordBandHardLimits")}
            />
            <BandWeightsRule
              {...common("bandWeights")}
              value={data.rules.bandWeights}
              factoryValue={data.factory.bandWeights}
              choices={data.layoutChoices}
              onSave={saveRule("bandWeights")}
            />
            <LayoutNumberMapRule
              {...common("minItemsByLayout")}
              ruleKey="minItemsByLayout"
              title="Fewest items a generated screen may have"
              description={
                <>
                  The fewest tabs, steps or icons the import may generate a screen with. A kind of
                  screen with no figure of its own takes no floor from this setting &mdash; its
                  own shape still applies, and where the two differ the screen&rsquo;s own shape
                  is the one that holds. Where the document cannot fill a screen to this, the
                  import uses a different kind of screen rather than padding it out.
                </>
              }
              bounds={COURSE_IMPORT_NESTED_BOUNDS.layoutMinItems}
              ownFigureLabel="Fewest items"
              defaultFigure={COURSE_IMPORT_NESTED_BOUNDS.layoutMinItems.min}
              effective={(id, draft) =>
                Object.prototype.hasOwnProperty.call(draft, id)
                  ? `at least ${draft[id]} items`
                  : "no floor from this setting — the screen's own shape applies"
              }
              factoryLabel={
                Object.keys(data.factory.minItemsByLayout).length
                  ? Object.entries(data.factory.minItemsByLayout)
                      .map(
                        ([id, n]) =>
                          `${data.layoutChoices.find((c) => c.id === id)?.label ?? id} at least ${n}`,
                      )
                      .join(" · ")
                  : "no floors"
              }
              value={data.rules.minItemsByLayout}
              factoryValue={data.factory.minItemsByLayout}
              choices={data.layoutChoices}
              onSave={saveRule("minItemsByLayout")}
            />
            <NumberRule
              {...common("maxSequenceDots")}
              ruleKey="maxSequenceDots"
              title="Most dots on a step-by-step screen"
              description="How many dots a step-by-step screen may carry, counting every one of them — the content steps and any closing question with its feedback. Where a passage has more steps than this, the import is asked for fewer, fuller steps rather than an extra one. It never drops the document's text to fit and never splits one idea in two to reach the count."
              bounds={COURSE_IMPORT_RULE_BOUNDS.maxSequenceDots}
              value={data.rules.maxSequenceDots}
              factoryValue={data.factory.maxSequenceDots}
              unit="dot"
              onSave={saveRule("maxSequenceDots")}
            />
            {lastChanged(data)}
          </>
        );

      case "video":
        return (
          <>
            <Note>
              A video screen carries a script somebody reads aloud. No video is ever generated —
              a generated video screen gets the existing stand-in reel.
            </Note>
            <NumberRule
              {...common("minVideoScreensPerCourse")}
              ruleKey="minVideoScreensPerCourse"
              title="Minimum video screens in a course"
              description="How many video screens a whole course should have. The import looks for the passages a person would actually say out loud — a welcome, a scenario, one idea explained end to end. If the document has fewer such passages than this, it says so and stops there rather than turning a table into a video. Zero switches the rule off."
              bounds={COURSE_IMPORT_RULE_BOUNDS.minVideoScreensPerCourse}
              value={data.rules.minVideoScreensPerCourse}
              factoryValue={data.factory.minVideoScreensPerCourse}
              unit="video screen"
              onSave={saveRule("minVideoScreensPerCourse")}
            />
            <NumberRule
              {...common("maxVideoScreensPerModule")}
              ruleKey="maxVideoScreensPerModule"
              title="Most video screens in one module"
              description="How many video screens one module should have. ⚠️ This is REPORTED, not enforced: a module over it is listed after the course is built, and changing this number will not make the import produce more videos — what it places is decided by the minimum above and by which passages read as something a person would say out loud. The two video kinds count as one. Zero means a module is not measured against a limit."
              bounds={COURSE_IMPORT_RULE_BOUNDS.maxVideoScreensPerModule}
              value={data.rules.maxVideoScreensPerModule}
              factoryValue={data.factory.maxVideoScreensPerModule}
              unit="video screen"
              onSave={saveRule("maxVideoScreensPerModule")}
            />
            {lastChanged(data)}
          </>
        );

      case "coverage":
        return (
          <>
            <Note>
              Two checks compare an import against the source document. The first runs while the
              course is planned: it makes sure every part of the document is claimed by a screen,
              and recovers anything the plan left out. The second runs after the course is saved:
              it measures how much of the document&rsquo;s text is actually in the saved course and
              lists any passage that is missing, with a 98% target. The second check is free,
              always runs, and cannot be switched off &mdash; the switch below governs the first.
            </Note>
            <SwitchRule
              {...common("coverageCheck")}
              ruleKey="coverageCheck"
              title="The recovery pass over the document"
              description="Whether the planning check runs and repairs what it finds. It takes longer and can cost more (a model is asked where a missed passage belongs; anything it declines is placed by position, which costs nothing). It never invents anything — it reports what it did."
              value={data.rules.coverageCheck}
              factoryValue={data.factory.coverageCheck}
              onText="On — every import is checked against the whole document while it is planned, and what was left out is recovered."
              offText="Off — the plan is whatever the first pass produced. The after-save measurement still runs and still reports what is missing."
              onSave={saveRule("coverageCheck")}
            />
            {lastChanged(data)}
          </>
        );

      case "fixed":
        return (
          <>
            <Note>
              These hold whatever the settings on the other tabs say. Each one names something the
              code does not do, rather than something it is configured not to do — so there is no
              setting that could switch any of them off.
            </Note>
            {data.invariants.map((inv) => (
              <Invariant key={inv.title} title={inv.title} body={inv.body} />
            ))}
          </>
        );

      case "sent":
        return (
          <>
            <Note>
              Written by the same code the import itself uses, so this is the real instruction and
              not a description of one. It is added to the standing instructions that always apply.
            </Note>
            {/* ⚠️ NO COUNT IN THIS SENTENCE. It said "two of the seven" while
                the screen held nineteen settings — a number beside the list it
                counts is a second copy of that list, and it drifts the first
                time somebody adds to one and not the other. */}
            <Note>
              Not every setting appears below, and that is deliberate. Alternating image placement
              and the second pass over the document are carried out by the software itself, and the
              fewest-items floor is checked by it &mdash; asking the AI to do something already
              decided invites it to do it differently. The step-by-step dot limit and the permitted
              answer counts go into a different instruction: the one that writes the words on each
              screen, rather than the one that plans the course.
            </Note>
            <pre style={CODE_STYLE}>{data.promptBlock}</pre>
          </>
        );
      case "runs":
        return <ImportRunsPanel api={api} />;
    }
  };

  return (
    <div className="console-page">
      <h1 style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 600, color: "var(--text-strong)" }}>
        Course import
      </h1>
      <Note>
        The rules an automatic course generation obeys. Saving changes what the next import
        produces — it never rewrites a course that already exists and never touches an
        author&rsquo;s edits.
      </Note>

      {/* ★ ABOVE the tab strip, and outside the loaded state, so the message
          survives the reload that proves it
          (`feedback_a_result_message_must_outlive_the_refresh_that_proves_it`). */}
      {saveNote && (
        <div className="card" style={{ padding: "10px 12px", marginTop: 12, maxWidth: 720 }}>
          <span className="pill accepted" style={{ marginRight: 8 }}>
            Saved
          </span>
          <span style={{ fontSize: 12.5, color: "var(--text)" }}>{saveNote}</span>
        </div>
      )}

      <Tabs tabs={IMPORT_TABS} current={tab} onSelect={setTab} idPrefix={TAB_PREFIX} />

      <TabPanel id={tab} idPrefix={TAB_PREFIX}>
        {state.t === "loading" && <Loading label="the course import rules" />}
        {state.t === "err" && <ErrorState err={state.err} onRetry={retry} />}
        {state.t === "ok" && body(state.data)}
      </TabPanel>
    </div>
  );
}
